<template>
  <div class="onboarding-connect-pages">
    <!-- Left selection : steps -->
    <OnboardingSteps
      :current-step="'pages'"
      :current-network="currentNetwork"
      @network-selected="handleNetworkSelected"
    />

    <!-- Right section : pages connection -->
    <div class="onboarding-content">
      <!-- Title section -->
      <div class="onboarding-header">
        <img
          :src="currentNetworkIcon"
          :alt="currentNetworkName"
          class="onboarding-network-icon"
        />
        <div class="onboarding-header-content">
          <h1 class="onboarding-header-title" v-if="viewMode === 'api'">
            {{ $t('onboarding.connect_pages.connection_title', { networkName: currentNetworkName }) }}
          </h1>
          <h1 class="onboarding-header-title" v-else>
            {{ $t('onboarding.connect_pages.connection_title_native', { networkName: currentNetworkName }) }}
            <svg class="native-icon" width="26" height="26" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" stroke-width="3" stroke="none" fill="#111827"><path d="M37.34,7.36a.12.12,0,0,1,.18.13c-.47,1.86-2.78,12.63,5.57,19.62,8.16,6.84,8.41,17.13,2.33,24-7.27,8.23-19.84,6.78-25.25,1.37C16.36,48.69,9.44,36.33,21.29,26a.1.1,0,0,1,.16,0c.29,1.23,2.3,9,7.66,10,.25,0,.37-.11.25-.34C27.78,32.6,20.66,17,37.34,7.36Z" stroke-linecap="round"/></svg>
          </h1>
          <p class="onboarding-header-subtitle" v-if="viewMode === 'api'">
            {{ $t('onboarding.connect_pages.connection_subtitle', { networkName: currentNetworkName }) }}
          </p>
          <p class="onboarding-header-subtitle" v-else>
            {{ $t('onboarding.connect_pages.connection_subtitle_native', { networkName: currentNetworkName }) }}
          </p>
        </div>
      </div>

      <!--- Features badges selected-->
      <div class="onboarding-features-badges" v-if="currentFeaturesBadges.length > 0">
        <span
          class="onboarding-feature-badge"
          v-for="feature in currentFeaturesBadges"
          :key="feature"
          :data-tooltip="$t(`onboarding.features.${feature}.description`)"
        >
          {{ $t(`onboarding.features.${feature}.title`) }}
        </span>
      </div>

      <!-- Quota reached warning banner (API view only) -->
      <div class="onboarding-quota-warning" v-if="viewMode === 'api' && hasReachedPageQuota">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="#F59E0B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M10 6V10" stroke="#F59E0B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M10 14H10.01" stroke="#F59E0B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <p>{{ $t('onboarding.connect_pages.quota_banner_reached') }}</p>
      </div>

      <!-- Connected pages list (API VIEW) -->
      <div class="onboarding-pages-list" v-if="viewMode === 'api'">
        <!--- Standard Connection card (For all networks including Instagram API) -->
        <div
          class="onboarding-page-card onboarding-add-page-card"
          :class="{ 'disabled': hasReachedPageQuota }"
          @click="connectNewPage"
        >
          <div class="onboarding-add-icon">+</div>
          <div class="onboarding-add-text">
            <span>{{ $t('onboarding.connect_pages.connect_new_page') }}</span>
          </div>
        </div>

        <!--- Connected pages cards (API view: selectable) -->
        <div
          class="onboarding-page-card"
          :class="{ 
            'selected': isPageSelected(page.id),
            'pre-existing': isPagePreExisting(page.id),
            'has-active-tooltip': hoveredPageId === page.id
          }"
          v-for="page in connectedNetworkPages"
          :key="page.id"
          @click="togglePageSelection(page.id)"
        >
          <!-- Pre-existing badge -->
          <div v-if="isPagePreExisting(page.id)" class="pre-existing-badge">
            {{ getPreExistingBadgeText(page) }}
          </div>
          
          <!-- Main Card Row (Avatar + Info + Checkmark) -->
          <div class="onboarding-page-card-main">
            <img
              v-if="page.avatar && !imageLoadErrors[page.id]"
              :src="page.avatar"
              :alt="page.name"
              class="onboarding-page-avatar"
              @error="handleImageError(page.id)"
            />
            <div
              v-else
              class="onboarding-page-avatar avatar-placeholder"
              role="img"
              :aria-label="page.name"
            >
              {{ page.name.charAt(0).toUpperCase() }}
            </div>
            <div class="onboarding-page-info">
              <div class="onboarding-page-name" :title="page.name">{{ page.name }}</div>
              <div v-if="page.username && !/^\d+$/.test(page.username)" class="onboarding-page-username" :title="page.username">@{{ page.username }}</div>
            </div>
            <div class="onboarding-selection-indicator">
              <div 
                class="onboarding-selection-check"
                :class="{ 'checked': isPageSelected(page.id) }"
              ></div>
            </div>
          </div>

          <!-- Active Native Badge ONLY on API view (no action button) -->
          <div 
            v-if="isPageNativeConnected(page)" 
            class="onboarding-page-native-footer"
            @click.stop
          >
            <div class="onboarding-native-badge-active">
              <svg class="native-icon-active" width="16" height="16" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="#059669">
                <path d="M37.34,7.36a.12.12,0,0,1,.18.13c-.47,1.86-2.78,12.63,5.57,19.62,8.16,6.84,8.41,17.13,2.33,24-7.27,8.23-19.84,6.78-25.25,1.37C16.36,48.69,9.44,36.33,21.29,26a.1.1,0,0,1,.16,0c.29,1.23,2.3,9,7.66,10,.25,0,.37-.11.25-.34C27.78,32.6,20.66,17,37.34,7.36Z" stroke-linecap="round"/>
              </svg>
              <span>{{ $t('onboarding.connect_pages.native_active') }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Connected pages list (NATIVE VIEW) -->
      <div class="onboarding-pages-list" v-else>
        <!-- Direct Native Connection card (For Facebook, Instagram, LinkedIn and X/Twitter) -->
        <div
          v-if="supportsDirectNativeAdd"
          class="onboarding-page-card onboarding-add-page-card"
          :class="{ 'disabled': hasReachedPageQuota }"
          @click="connectDirectNativePage"
        >
          <div class="onboarding-add-icon">+</div>
          <div class="onboarding-add-text">
            <span>{{ directNativeAddText }}</span>
          </div>
        </div>

        <!--- Connected pages cards (Native view: not deselectable, selected style preserved) -->
        <div
          class="onboarding-page-card selected native-view-card"
          v-for="page in nativeNetworkPages"
          :key="page.id"
        >
          <!-- Main Card Row (Avatar + Info + Checkmark) -->
          <div class="onboarding-page-card-main">
            <img
              v-if="page.avatar && !imageLoadErrors[page.id]"
              :src="page.avatar"
              :alt="page.name"
              class="onboarding-page-avatar"
              @error="handleImageError(page.id)"
            />
            <div
              v-else
              class="onboarding-page-avatar avatar-placeholder"
              role="img"
              :aria-label="page.name"
            >
              {{ page.name.charAt(0).toUpperCase() }}
            </div>
            <div class="onboarding-page-info">
              <div class="onboarding-page-name" :title="page.name">{{ page.name }}</div>
              <div v-if="page.username && !/^\d+$/.test(page.username)" class="onboarding-page-username" :title="page.username">@{{ page.username }}</div>
            </div>
            <div class="onboarding-selection-indicator">
              <div class="onboarding-selection-check checked"></div>
            </div>
          </div>

          <!-- Native Connection Action / Badge -->
          <div class="onboarding-page-native-footer">
            <!-- Active Native Badge -->
            <div v-if="isPageNativeConnected(page)" class="onboarding-native-badge-active">
              <svg class="native-icon-active" width="16" height="16" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="#059669">
                <path d="M37.34,7.36a.12.12,0,0,1,.18.13c-.47,1.86-2.78,12.63,5.57,19.62,8.16,6.84,8.41,17.13,2.33,24-7.27,8.23-19.84,6.78-25.25,1.37C16.36,48.69,9.44,36.33,21.29,26a.1.1,0,0,1,.16,0c.29,1.23,2.3,9,7.66,10,.25,0,.37-.11.25-.34C27.78,32.6,20.66,17,37.34,7.36Z" stroke-linecap="round"/>
              </svg>
              <span>{{ $t('onboarding.connect_pages.native_active') }}</span>
            </div>

            <!-- Activate Native Button (without info icon) -->
            <div v-else class="onboarding-native-btn-wrapper">
              <button 
                class="onboarding-native-action-btn" 
                @click="handleNativeAction(page)"
              >
                <svg class="native-icon-btn" width="18" height="18" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                  <path d="M37.34,7.36a.12.12,0,0,1,.18.13c-.47,1.86-2.78,12.63,5.57,19.62,8.16,6.84,8.41,17.13,2.33,24-7.27,8.23-19.84,6.78-25.25,1.37C16.36,48.69,9.44,36.33,21.29,26a.1.1,0,0,1,.16,0c.29,1.23,2.3,9,7.66,10,.25,0,.37-.11.25-.34C27.78,32.6,20.66,17,37.34,7.36Z" stroke-linecap="round"/>
                </svg>
                <span>{{ $t('onboarding.connect_pages.activate_native') || 'Activer en Natif ?' }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <!--- Footer with NAV buttons-->
      <div class="onboarding-footer-container" :class="{ 'is-native-view': viewMode === 'native' }">
        <!-- API View: Pages counter -->
        <div class="onboarding-pages-counter" v-if="viewMode === 'api'">
          <span v-if="selectedPageIds.length === 1">
            {{ selectedPageIds.length }} 
            {{ $t('onboarding.connect_pages.page_selected_singular') }}
          </span>
          <span v-else>
            {{ selectedPageIds.length }} 
            {{ $t('onboarding.connect_pages.page_selected_plural') }}
          </span>
        </div>

        <div class="onboarding-footer">
          <button 
            v-if="viewMode === 'native'"
            class="onboarding-btn-secondary" 
            @click="backToApiView"
          >
            {{ $t('onboarding.connect_pages.back_to_pages') }}
          </button>
          <button
            class="onboarding-btn-primary"
            @click="goToNextStep"
            :disabled="!canProceed || isSavingNetwork"
          >
            <span v-if="isSavingNetwork" class="btn-spinner"></span>
            <span>{{ $t('onboarding.connect_pages.next_step') }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Loading overlay on OAuth discovery -->
    <LoadingSpinner
      :show="isConnecting"
      :message="$t('onboarding.connect_pages.retrieving_pages')"
    />

    <!-- Light saving overlay when writing selection to BDD -->
    <LoadingSpinner
      :show="isSavingNetwork"
      :message="$t('onboarding.connect_pages.saving_pages', { network: savingNetworkName })"
      hide-percent
    />

    <!-- Native Deselect Confirmation Modal -->
    <div v-if="showNativeDeselectModal" class="modal-popup" @click="cancelNativeDeselect">
      <div class="popup confirmation-popup" @click.stop>
        <div class="modal-warning-icon">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#d97706" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        </div>
        <h3 class="modal-title">{{ $t('onboarding.notifications.info_title') }}</h3>
        <p class="modal-text">
          {{ deselectModalType === 'native' ? $t('onboarding.connect_pages.native_deselect_warning') : $t('onboarding.connect_pages.preexisting_deselect_warning') }}
        </p>
        <div class="buttons modal-buttons">
          <div class="button btn-outline-primary" @click="cancelNativeDeselect">
            {{ $t('onboarding.connect_pages.modal_deselect_cancel') }}
          </div>
          <div class="button btn-primary" @click="confirmNativeDeselect">
            {{ $t('onboarding.connect_pages.modal_deselect_confirm') }}
          </div>
        </div>
      </div>
    </div>

    <!-- Bluesky authentication modal -->
    <BlueskyModal
      :active="blueskyAuth.active"
      @connect="handleBlueskyConnect"
      @cancel="handleBlueskyCancel"
      ref="blueskyModal"
    />

    <!-- Google refresh token modal -->
    <GoogleRefreshModal
      :active="googleRefresh.active"
      @reconnect="handleGoogleReconnect"
      @skip="handleGoogleRefreshSkip"
    />
    
    <!-- Instagram Direct Native Connection modal -->
    <InstagramNativeModal
      :active="showInstagramNativeModal"
      :initial-username="targetInstagramUsername"
      :error="instaNativeError"
      :is-connecting="isInstaNativeConnecting"
      @connect="handleInstagramNativeConnect"
      @cancel="closeInstagramNativeModal"
    />

    <!-- Two-Factor Authentication Modal for Instagram -->
    <TwoFactorModal
      :active.sync="twoFactorActive"
      :page="refreshedInstaPage"
      @connected="handleTwoFactorSuccess"
      @success="handleTwoFactorSuccess"
      @cancel="twoFactorActive = false"
    />

    <!-- Exit confirmation modal -->
    <modal-popup />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { EventBus } from '@/event-bus';
import moment from 'moment';
import Common from '../../mixins/Common.vue';
import Notif from '../../mixins/Notif.vue';
import Pages from '../../mixins/Pages.vue';
import Social from '../../mixins/Social.vue';
import User from '../../mixins/User.vue';
import Bots from '../../mixins/Bots.vue';
import OnboardingSteps from '../shared/OnboardingSteps.vue';
import LoadingSpinner from '../shared/LoadingSpinner.vue';
import BlueskyModal from '../shared/BlueskyModal.vue';
import GoogleRefreshModal from '../shared/GoogleRefreshModal.vue';
import TwoFactorModal from '../shared/TwoFactorModal.vue';
import InstagramNativeModal from '../shared/InstagramNativeModal.vue';
import modalPopup from '@/components/dash/modalPopup.vue';
import selectPage from '../../../store/modules/selectPage';
import Lock from '@/utils/Lock.js';
import { persistOnboardingPages, restoreOnboardingPages, clearOnboardingFlags } from '../utils/onboardingPersistence';
import { getNetworkName, getNetworkIcon } from '../utils/networkMaps';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuid = (str) => typeof str === 'string' && UUID_REGEX.test(str);

export default {
  name: 'StepConnectPages',

  components: {
    OnboardingSteps,
    LoadingSpinner,
    BlueskyModal,
    GoogleRefreshModal,
    TwoFactorModal,
    InstagramNativeModal,
    modalPopup
  },

  mixins: [Pages, Social, Common, Notif, User, Bots],

  data() {
    const savedIndex = localStorage.getItem('onboarding_current_index')
    const isOAuthReturn = localStorage.getItem('onboarding_mode') === 'true'
    return {
      currentNetworkIndex: isOAuthReturn && savedIndex !== null ? parseInt(savedIndex, 10) : 0,
      selectedPageIds: [],
      hoveredPageId: null,
      oauthCallbackProcessed: false, // Prevent duplicate OAuth callback processing
      isConnecting: false, // Loading state to prevent race conditions
      imageLoadErrors: {}, // Track failed avatar images
      showInstagramNativeModal: false,
      targetInstagramPage: null, // Tracks target page when activating native on an existing card
      isInstaNativeConnecting: false,
      instaNativeError: null,
      instaNativeCredentials: {
        username: '',
        password: ''
      },
      twoFactorActive: false,
      refreshedInstaPage: null,
      blueskyAuth: {
        active: false
      },
      googleRefresh: {
        active: false
      },
      loadingMessage: '', // Message displayed during connection
      userPageQuota: 10, // Will be calculated based on user plan
      
      // OAuth provider registry for better maintainability
      oauthProviders: {
        pinterest: {
          type: 'oauth2',
          detectBy: 'currentNetwork', // Detect by restored currentNetwork from localStorage
          verifyMethod: 'postPinterestVerify',
          successMessage: 'onboarding.connect_pages.success_pinterest'
        },
        linkedin: {
          type: 'oauth2',
          detectBy: 'currentNetwork',
          verifyMethod: 'postLinkedinVerify',
          successMessage: 'onboarding.connect_pages.success_linkedin'
        },
        youtube: {
          type: 'oauth2',
          detectBy: 'currentNetwork',
          verifyMethod: 'postYoutubeVerify',
          successMessage: 'onboarding.connect_pages.success_youtube'
        },
        tiktok: {
          type: 'oauth2',
          detectBy: 'currentNetwork',
          verifyMethod: 'postTiktokVerify',
          requiresState: true,
          successMessage: 'onboarding.connect_pages.success_tiktok'
        },
        twitter: {
          type: 'oauth2',
          detectBy: 'currentNetwork', // Twitter OAuth 2.0 (version 2)
          verifyMethod: 'newPage', // Directly call newPage with code
          usesNewPageDirectly: true, // Special flag to indicate newPage() is the verify method
          successMessage: 'onboarding.connect_pages.success_twitter'
        },
        threads: {
          type: 'oauth2',
          detectBy: 'state', // Detect by state parameter
          stateValue: 'threads-connect',
          verifyMethod: 'postThreadsVerify',
          successMessage: 'onboarding.connect_pages.success_threads'
        },
        instagram: {
          type: 'oauth2',
          detectBy: 'state',
          stateValue: 'ig_business-connect',
          verifyMethod: 'postInstagramBusinessVerify',
          successMessage: 'onboarding.connect_pages.success_instagram_business'
        },
        x: {
          type: 'oauth1',
          verifyMethod: 'addTwitterV1',
          successMessage: 'onboarding.connect_pages.success_x'
        }
      },
      isSavingNetwork: false,
      savingNetworkName: '',
      showNativeDeselectModal: false,
      pageToDeselect: null,
      deselectModalType: null,
      viewMode: 'api'
    }
  },

  computed: {
    ...mapState('onboarding', [
      'selectedNetworks',
      'selectedFeatures',
      'connectedPages',
      'selectedPageIds',
      'preExistingPageIds'
    ]),
    ...mapGetters('onboarding', ['totalConnectedPages']),

    currentNetwork() {
      return this.selectedNetworks[this.currentNetworkIndex] || null
    },

    currentNetworkName() {
      return getNetworkName(this.currentNetwork)
    },

    currentNetworkIcon() {
      return getNetworkIcon(this.currentNetwork)
    },

    isCurrentNetworkNativeFeatureSelected() {
      if (!this.currentNetwork) return false
      const key = this.currentNetwork === 'x' ? 'twitter.native' : `${this.currentNetwork}.native`
      return this.selectedFeatures.includes(key) || (this.currentNetwork === 'twitter' && this.selectedFeatures.includes('x.native'))
    },

    currentFeaturesBadges() {
      if (this.viewMode === 'native') {
        return this.getNativeFeatureBadgesForNetwork(this.currentNetwork)
      }
      return this.networkFeatures
    },

    nativeNetworkPages() {
      return this.connectedNetworkPages.filter(page => this.isPageSelected(page.id))
    },

    supportsDirectNativeAdd() {
      return ['facebook', 'instagram', 'linkedin', 'twitter', 'x'].includes(this.currentNetwork)
    },

    directNativeAddText() {
      if (['instagram', 'twitter', 'x'].includes(this.currentNetwork)) {
        return this.$t('onboarding.connect_pages.connect_direct_native_pages') || 'Ajouter des pages en Natif'
      }
      return this.$t('onboarding.connect_pages.connect_direct_native_pages_or_groups') || 'Ajouter des pages, groupes ou profil en Natif'
    },

    connectedNetworkPages() {
      return this.connectedPages[this.currentNetwork] || []
    },

    targetInstagramUsername() {
      if (this.targetInstagramPage) {
        const raw = this.targetInstagramPage.username || this.targetInstagramPage.name || ''
        return raw.trim().replace(/[\s@]/g, '')
      }
      return ''
    },

    isInstagramNativeFeatureSelected() {
      return this.selectedFeatures.includes('instagram.native')
    },

    // Features to display as badges for the current network
    // IMPORTANT: Only show UNIVERSAL features here (OAuth connection)
    // Network-specific features (native connection) are shown in StepConnectNatives / Extension
    networkFeatures() {
      // Universal features available via OAuth connection
      const universalFeatures = ['planning', 'reporting', 'moderation']
      
      // Networks that do NOT support certain features
      const unsupportedFeatures = {
        twitter: ['reporting', 'moderation'],
        google: ['reporting', 'moderation'],
        youtube: ['reporting', 'moderation'],
        threads: ['reporting', 'moderation'],
        bluesky: ['reporting', 'moderation'],
        pinterest: ['reporting', 'moderation']
      }
      
      const excluded = unsupportedFeatures[this.currentNetwork] || []
      
      // Filter selected features to only show universal ones supported by this network
      return this.selectedFeatures.filter(f => universalFeatures.includes(f) && !excluded.includes(f))
    },

    currentStepIndex() {
      return this.currentNetworkIndex + 1
    },

    totalNetworks() {
      return this.selectedNetworks.length
    },

    canProceed() {
      return true
    },

    remainingPagesCount() {
      return this.maxSelectedPages - this.selectedPageIds.length
    },

    // Calculate user's page quota based on their plan
    userPlanPageQuota() {
      // If user profile is not loaded yet, do not restrict quota to avoid race conditions
      if (this.user === undefined) return 999
      // Lock.rs_limit defines social accounts limit per plan (name is misleading — not RSS only)
      // Plan tiers: 0 = Free, 1 = Starter, 2 = Standard, 3 = Premium
      const plan = this.getUserPlan || 0 // Default to free plan if no user
      const quotas = Lock.rs_limit // [1, 10, 60, 250] — social accounts limit
      
      console.log('[StepConnectPages] User plan:', plan)
      console.log('[StepConnectPages] Page quota for plan:', quotas[plan])
      
      return quotas[plan] || 10 // Default to 10 if plan not found
    },

    // Calculate maximum selected pages allowed across all networks
    maxSelectedPages() {
      return this.userPlanPageQuota
    },

    // Total pages across ALL networks (all connected, selected or not)
    totalPagesAcrossAllNetworks() {
      return Object.values(this.connectedPages).reduce((sum, pages) => sum + pages.length, 0)
    },

    // Total SELECTED pages across ALL networks (for quota display)
    // Matches OnboardingSteps.vue logic: counts only pages that exist in connectedPages
    // AND are selected/pre-existing (prevents counting failed connections)
    totalSelectedPages() {
      return this.$store.getters['onboarding/totalSelectedPages']
    },

    // Check if user has reached their page quota (based on SELECTED pages)
    hasReachedPageQuota() {
      return this.totalSelectedPages >= this.userPlanPageQuota
    },

    // Check if user has selected features requiring native connection for current network
    nativeFeaturesForNetwork() {
      if (!this.currentNetwork) return []
      
      const hasNative = this.selectedFeatures.some(f => 
        f === `${this.currentNetwork}.native` || f === 'twitter.native' || f === 'x.native'
      )
      
      return hasNative ? [`${this.currentNetwork}.native`] : []
    },

    requiresNativeConnection() {
      // Instagram direct native connection is now handled directly in StepConnectPages
      return false
    },

    requiresExtension() {
      // LinkedIn, TikTok, Facebook, Twitter and X require web extension
      const extensionNetworks = ['linkedin', 'tiktok', 'facebook', 'twitter', 'x']
      
      // Check if network requires extension
      if (!extensionNetworks.includes(this.currentNetwork)) {
        return false
      }
      
      // Check if user selected native features
      if (this.nativeFeaturesForNetwork.length === 0) {
        return false
      }
      
      // Check if any selected pages DON'T have native connection yet
      const selectedPages = this.connectedNetworkPages.filter(p => 
        this.selectedPageIds.includes(p.id)
      )
      
      const pagesNeedingExtension = selectedPages.filter(page => {
        const hasNativeConnection = page.story_config && page.story_config.created === true
        return !hasNativeConnection // Only pages without native connection
      })
      
      console.log('[StepConnectPages] requiresExtension check:', {
        network: this.currentNetwork,
        nativeFeatures: this.nativeFeaturesForNetwork.length,
        selectedPages: selectedPages.length,
        pagesNeedingExtension: pagesNeedingExtension.length
      })
      
      return pagesNeedingExtension.length > 0
    }
  },

  watch: {
    // Automatically sync selectedPageIds when user switches networks
    currentNetwork: {
      immediate: false, // Don't run on mount (handled by loadSelectedPages in mounted())
      handler(newNetwork) {
        console.log('[StepConnectPages] Network changed to:', newNetwork)
        this.loadSelectedPages()
      }
    },

    // Sync local selectedPageIds from Vuex store updates in real-time
    '$store.state.onboarding.selectedPageIds': {
      deep: true,
      handler(newVal) {
        const savedPageIds = newVal[this.currentNetwork] || []
        if (JSON.stringify(this.selectedPageIds) !== JSON.stringify(savedPageIds)) {
          console.log('[StepConnectPages] Watcher: syncing local selectedPageIds from store:', savedPageIds)
          this.selectedPageIds = [...savedPageIds]
        }
      }
    },

    // Persist current network index so plain page reloads return to the right network
    currentNetworkIndex(val) {
      localStorage.setItem('onboarding_current_index', val.toString())
    },

    '$route.params': {
      immediate: false,
      handler(params) {
        if (params && params.network) {
          let networkIndex = this.selectedNetworks.indexOf(params.network)
          if (networkIndex === -1) {
            if (params.network === 'twitter') networkIndex = this.selectedNetworks.indexOf('x')
            else if (params.network === 'x') networkIndex = this.selectedNetworks.indexOf('twitter')
          }
          if (networkIndex !== -1) {
            this.currentNetworkIndex = networkIndex
          }
        }
        if (params && (params.viewMode === 'native' || params.fromExtensions === true)) {
          if (this.isCurrentNetworkNativeFeatureSelected) {
            this.viewMode = 'native'
          } else {
            this.viewMode = 'api'
          }
        }
      }
    },

    // Deferred initialization for plain page reloads.
    // Child mounts before parent (Onboarding.vue), so selectedNetworks is empty when mounted()
    // fires. This watcher triggers once the parent has restored Vuex from localStorage.
    selectedNetworks: {
      handler(networks) {
        if (!this._deferredInit || networks.length === 0) return
        this._deferredInit = false
        console.log('[StepConnectPages] selectedNetworks populated after reload - running deferred init')

        // Restore network index (saved by currentNetworkIndex watcher above)
        const savedIndex = localStorage.getItem('onboarding_current_index')
        if (savedIndex !== null) {
          const parsed = Number.parseInt(savedIndex)
          if (!Number.isNaN(parsed) && parsed > 0 && parsed < networks.length) {
            console.log('[StepConnectPages] Restoring network index:', parsed, '→', networks[parsed])
            this.currentNetworkIndex = parsed
          }
        }

        this.loadExistingPages().then(() => this.loadSelectedPages())
      }
    }
  },

  methods: {
    supportsExtensionNative(network) {
      const nativeNetworks = ['facebook', 'instagram', 'linkedin', 'tiktok', 'twitter', 'x']
      return nativeNetworks.includes(network)
    },

    isPageNativeConnected(page) {
      if (!page) return false
      const nativeConnections = this.$store.state.onboarding.nativeConnections || {}
      const statusById = nativeConnections[page.id]
      const statusByNetworkId = page.network_id ? nativeConnections[page.network_id] : null
      
      if (statusById && statusById.connected === true) return true
      if (statusByNetworkId && statusByNetworkId.connected === true) return true
      
      return !!(page.story_config && (page.story_config.created === true || page.story_config.onlyConciergerie === true || page.story_config.session_state))
    },

    handleNativeAction(page) {
      if (this.currentNetwork === 'instagram' || (page && page.network === 'instagram')) {
        this.openInstagramNativeModal(page)
      } else {
        this.goToExtensionForPage(page)
      }
    },

    connectDirectNativePage() {
      if (this.isConnecting) return
      if (this.hasReachedPageQuota) {
        this.notify({
          time: 5000,
          title: this.$t('onboarding.connect_pages.quota_status'),
          text: this.$t('onboarding.connect_pages.quota_banner_reached'),
          color: 'warning'
        })
        return
      }

      if (this.currentNetwork === 'instagram') {
        this.openInstagramNativeModal(null)
      } else if (['facebook', 'linkedin', 'twitter', 'x'].includes(this.currentNetwork)) {
        this.goToExtensionForPage(null)
      }
    },

    goToExtensionForPage(page) {
      console.log('[StepConnectPages] Navigating to StepConnectExtensions for target page:', page ? page.name : 'direct add', page ? page.id : null)
      
      if (page && page.id) {
        localStorage.setItem('onboarding_target_page_id', page.id)
        localStorage.setItem('onboarding_target_page_name', page.name)
      } else {
        localStorage.removeItem('onboarding_target_page_id')
        localStorage.removeItem('onboarding_target_page_name')
      }
      
      localStorage.setItem('onboarding_mode', 'true')
      localStorage.setItem('onboarding_current_network', this.currentNetwork)
      localStorage.setItem('onboarding_current_index', this.currentNetworkIndex.toString())
      localStorage.setItem('onboarding_selected_networks', JSON.stringify(this.selectedNetworks))
      localStorage.setItem('onboarding_selected_features', JSON.stringify(this.selectedFeatures))
      this.persistOnboardingState()
      
      this.$router.push({
        name: 'onboarding-connectExtensions',
        params: { network: this.currentNetwork },
        query: { pageId: page ? page.id : undefined }
      })
    },

    getNativeFeatureBadgesForNetwork(network) {
      const map = {
        facebook: ['facebook.native_post', 'facebook.groups', 'facebook.personal'],
        instagram: ['instagram.native_post', 'instagram.stories', 'instagram.mention', 'instagram.music', 'instagram.hashtag', 'instagram.sticker'],
        linkedin: ['linkedin.native_post', 'linkedin.mentions', 'linkedin.groups', 'linkedin.carousel', 'linkedin.poll'],
        tiktok: ['tiktok.mentions', 'tiktok.reporting'],
        twitter: ['twitter.native_post', 'twitter.mentions'],
        x: ['twitter.native_post', 'twitter.mentions']
      }
      return map[network] || []
    },

    openNativeInfoModal(network) {
      const networkName = this.currentNetworkName
      const featureKeys = this.getNativeFeatureBadgesForNetwork(network)
      const badgesHtml = featureKeys.map(f => 
        `<span style="display:inline-block; background:#f1edf5; color:#6a1f4c; padding:6px 12px; border-radius:14px; font-size:12px; font-weight:600; margin:4px;">${this.$t(`onboarding.features.${f}.title`)}</span>`
      ).join('')

      const modalHtml = `
        <p style="margin-bottom: 16px; color: #4b5563; font-size: 14px; text-align: center; line-height: 1.5;">
          ${this.$t('onboarding.connect_pages.unlocked_features_title')}
        </p>
        <div style="display:flex; flex-wrap:wrap; justify-content:center; gap:6px; padding:16px; background:#faf5f8; border-radius:12px; border:1px solid #fbcfe8;">
          ${badgesHtml}
        </div>
      `

      EventBus.$emit('showModal',
        this.$t('onboarding.connect_pages.native_info_modal_title', { networkName }) || `Fonctionnalités Natif ${networkName}`,
        modalHtml,
        this.$t('onboarding.modale.exit.cancel') || 'Fermer',
        null,
        null,
        null
      )
    },

    handlePageShow(event) {
      console.log('[StepConnectPages] pageshow event fired, persisted:', event?.persisted)
      if (event?.persisted || this.isConnecting) {
        console.log('[StepConnectPages] Resetting isConnecting to false after pageshow')
        this.isConnecting = false
      }
    },

    connectNewPage() {
      if (this.isConnecting) return
      if (this.hasReachedPageQuota) {
        this.notify({
          time: 5000,
          title: this.$t('onboarding.connect_pages.quota_status'),
          text: this.$t('onboarding.connect_pages.quota_banner_reached'),
          color: 'warning'
        })
        return
      }
      
      localStorage.setItem('onboarding_mode', 'true')
      localStorage.setItem('onboarding_current_network', this.currentNetwork)
      localStorage.setItem('onboarding_current_index', this.currentNetworkIndex.toString())
      localStorage.setItem('onboarding_selected_networks', JSON.stringify(this.selectedNetworks))
      localStorage.setItem('onboarding_selected_features', JSON.stringify(this.selectedFeatures))
      
      // Use persistOnboardingState() for comprehensive state persistence
      this.persistOnboardingState()
      
      console.log('[StepConnectPages] localStorage flags set:', {
        network: this.currentNetwork,
        index: this.currentNetworkIndex
      })
      
      switch(this.currentNetwork) {
        case 'facebook':
          this.connectFB()
          break
        case 'instagram':
          this.connectInsta()
          break
        case 'linkedin':
          this.connectLinkedin({ refresh: false })
          break
        case 'twitter':
        case 'x':
          this.connectTwitter({ refresh: false, version: 2 })
          break
        case 'google':
          this.loginWithGoogle()
          break
        case 'threads':
          this.connectThreads()
          break
        case 'tiktok':
          this.connectTiktok({ refresh: false })
          break
        case 'youtube':
          this.connectYoutube()
          break
        case 'bluesky':
          this.loginWithBluesky()
          break
        case 'pinterest':
          this.connectPinterest()
          break
        default:
          console.warn('[StepConnectPages] Network not supported:', this.currentNetwork)
      }
    },

    openInstagramNativeModal(page = null) {
      if (!page && this.hasReachedPageQuota) {
        this.notify({
          time: 5000,
          title: this.$t('onboarding.connect_pages.quota_status'),
          text: this.$t('onboarding.connect_pages.quota_banner_reached'),
          color: 'warning'
        })
        return
      }

      this.hoveredPageId = null
      this.targetInstagramPage = page
      this.instaNativeCredentials = { 
        username: page ? (page.username || page.name || '') : '', 
        password: '' 
      }
      this.instaNativeError = null
      this.showInstagramNativeModal = true
    },

    closeInstagramNativeModal() {
      this.showInstagramNativeModal = false
      this.targetInstagramPage = null
      this.instaNativeCredentials = { username: '', password: '' }
      this.instaNativeError = null
    },

    async handleInstagramNativeConnect(credentials) {
      if (!credentials || !credentials.username || !credentials.password) return
      
      const rawUsername = credentials.username || ''
      const cleanUsername = rawUsername.trim().replace(/[\s@]/g, '')
      const cleanPassword = (credentials.password || '').trim()

      if (rawUsername.includes('@')) {
        this.instaNativeError = "Veuillez utiliser votre nom d'utilisateur Instagram (ex: nom_compte) et non votre adresse email."
        return
      }

      this.isInstaNativeConnecting = true
      this.instaNativeError = null
      
      // Helper to check for standard UUID format
      const isUuid = (str) => typeof str === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str)

      // Determine target pageId:
      // 1. If user clicked on a specific page card, use its id ONLY if it is already a valid UUID in DB
      // 2. Otherwise, check if cleanUsername matches an existing page that has a valid UUID
      let targetId = 'createPage'
      let matchedPage = null
      if (this.targetInstagramPage) {
        matchedPage = this.targetInstagramPage
        if (this.targetInstagramPage.id && isUuid(this.targetInstagramPage.id)) {
          targetId = this.targetInstagramPage.id
        }
      } else {
        const existingPages = this.connectedPages.instagram || []
        matchedPage = existingPages.find(p => 
          (p.username && p.username.toLowerCase() === cleanUsername.toLowerCase()) ||
          (p.name && p.name.toLowerCase() === cleanUsername.toLowerCase())
        )
        if (matchedPage && matchedPage.id && isUuid(matchedPage.id)) {
          targetId = matchedPage.id
        }
      }

      const body = {
        pageId: targetId,
        usernameAccount: cleanUsername,
        passwordAccount: cleanPassword
      }
      
      try {
        console.log('[StepConnectPages] Submitting Instagram Direct Native connection with pageId:', targetId)
        const response = await this.connectLinkedinDirect(body)
        
        if (response && response.body && response.ok) {
          const pageData = response.body
          
          if (pageData.connected) {
            console.log('[StepConnectPages] Instagram direct connection success')
            const finalPageId = (pageData.id && isUuid(pageData.id)) 
              ? pageData.id 
              : ((targetId !== 'createPage' && isUuid(targetId)) ? targetId : (pageData.id || credentials.username))

            const newPage = {
              id: finalPageId,
              network_id: credentials.username,
              name: pageData.name || (matchedPage ? matchedPage.name : credentials.username),
              username: credentials.username,
              avatar: pageData.avatar || (matchedPage ? matchedPage.avatar : null),
              story_config: pageData.story_config || null,
              hasUuid: true
            }

            const autoSelect = !this.hasReachedPageQuota
            this.addPageIfNotExists('instagram', newPage, autoSelect)
            
            this.$store.commit('onboarding/SET_NATIVE_CONNECTION', {
              network: 'instagram',
              pageId: finalPageId,
              connected: true,
              storyConfig: pageData.story_config
            })

            if (matchedPage && matchedPage.id && matchedPage.id !== finalPageId) {
              this.$store.commit('onboarding/SET_NATIVE_CONNECTION', {
                network: 'instagram',
                pageId: matchedPage.id,
                connected: true,
                storyConfig: pageData.story_config
              })
              this.$store.commit('onboarding/REPLACE_PAGE_ID', {
                network: 'instagram',
                oldPageId: matchedPage.id,
                newPageId: finalPageId
              })
            }
            
            this.closeInstagramNativeModal()
            this.notify({
              time: 5000,
              title: this.$t('onboarding.connect_natives.success_title'),
              text: this.$t('onboarding.connect_natives.success_message'),
              color: 'success'
            })
            
            await this.getPages()
          } else if (pageData.twoAuth || pageData.challengeInstagram || pageData.awaitingApproval) {
            console.log('[StepConnectPages] Instagram 2FA / Challenge / AwaitingApproval required')
            const pagesRes = await this.getPages()
            const allPages = (pagesRes && pagesRes.body && pagesRes.body.pages) || []
            const finalPageId = (pageData.id && isUuid(pageData.id))
              ? pageData.id 
              : ((targetId !== 'createPage' && isUuid(targetId)) ? targetId : credentials.username)
            
            // Look for page in backend response (where story_config was persisted in DB)
            const foundPage = allPages.find(p => p.id === finalPageId || p.network_id === credentials.username)
            
            const freshConfig = pageData.story_config || {
              two_factor: pageData.two_factor,
              checkpoint: pageData.challengeInstagram,
              awaitingApproval: pageData.awaitingApproval
            }
            
            const resolvedStoryConfig = (freshConfig && (freshConfig.two_factor || freshConfig.checkpoint || freshConfig.awaitingApproval))
              ? freshConfig
              : ((foundPage && foundPage.story_config) || freshConfig)

            this.refreshedInstaPage = {
              id: finalPageId,
              name: (foundPage && foundPage.name) || (matchedPage ? matchedPage.name : credentials.username),
              username: credentials.username,
              network: 'instagram',
              avatar: (foundPage && foundPage.avatar) || (matchedPage ? matchedPage.avatar : null),
              story_config: resolvedStoryConfig,
              twoAuth: true
            }
            
            // Close the credentials input modal so it is not visible behind the 2FA modal
            this.closeInstagramNativeModal()
            this.twoFactorActive = true
          } else {
            const isEmailInput = credentials.username.includes('@')
            if (isEmailInput) {
              this.instaNativeError = "Veuillez utiliser votre nom d'utilisateur Instagram (ex: nom_compte) et non votre adresse email."
            } else if (pageData.badPassword) {
              this.instaNativeError = this.$t('dash.bots.bad_password') || 'Nom d\'utilisateur ou mot de passe incorrect.'
            } else if (pageData.invalidUser) {
              this.instaNativeError = this.$t('dash.bots.invalid_user') || 'Nom d\'utilisateur introuvable.'
            } else if (pageData.captcha) {
              this.instaNativeError = this.$t('dash.bots.captcha') || 'Vérification de sécurité requise sur votre application Instagram.'
            } else if (pageData.feedback_required) {
              this.instaNativeError = "Action temporairement limitée par Instagram. Veuillez réessayer plus tard."
            } else {
              this.instaNativeError = this.$t('onboarding.connect_natives.error_credentials')
            }
          }
        } else {
          // Technical or server failure (HTTP 500, network loss, etc.) -> Clean user-friendly message
          console.error('[StepConnectPages] Instagram Direct connection HTTP error:', response)
          this.instaNativeError = this.$t('onboarding.connect_natives.error_network') || 'Une erreur est survenue lors de la connexion. Veuillez réessayer.'
        }
      } catch (e) {
        console.error('[StepConnectPages] Instagram native error:', e)
        this.instaNativeError = this.$t('onboarding.connect_natives.error_unknown')
      } finally {
        this.isInstaNativeConnecting = false
      }
    },

    async handleTwoFactorSuccess() {
      this.twoFactorActive = false
      this.closeInstagramNativeModal()
      
      const pagesRes = await this.getPages()
      const allPages = (pagesRes && pagesRes.body && pagesRes.body.pages) || []
      const targetPageId = this.refreshedInstaPage?.id
      const targetUsername = this.refreshedInstaPage?.username || this.refreshedInstaPage?.name
      
      const connectedPage = allPages.find(p => p.id === targetPageId || p.network_id === targetUsername)
      
      if (connectedPage) {
        const autoSelect = !this.hasReachedPageQuota
        this.addPageIfNotExists('instagram', {
          id: connectedPage.id,
          network_id: connectedPage.network_id || targetUsername,
          name: connectedPage.name,
          username: connectedPage.username || targetUsername,
          avatar: connectedPage.avatar,
          story_config: connectedPage.story_config,
          hasUuid: true
        }, autoSelect)
        
        this.$store.commit('onboarding/SET_NATIVE_CONNECTION', {
          network: 'instagram',
          pageId: connectedPage.id,
          connected: true,
          storyConfig: connectedPage.story_config
        })
      }
      
      this.saveSelectedPages()
      this.persistOnboardingState()
      
      this.notify({
        time: 5000,
        title: this.$t('onboarding.connect_natives.success_title'),
        text: this.$t('onboarding.connect_natives.success_message'),
        color: 'success'
      })
    },

    // Facebook connection method
    connectFB() {
      this.$ga.event('onboarding', 'connect-facebook')
      this.isConnecting = true
      
      // eslint-disable-next-line
      FB.login(
        response => {
          if (response.status === 'connected') {
            const accessToken = response.authResponse.accessToken

            // Validate token (same as dashboard)
            this.getUserInfoFb(accessToken).then(res => {
              if (!res.ok) {
                console.error('[StepConnectPages] getUserInfoFb failed:', res)
                this.isConnecting = false
                return
              }

              // Exchange for long-lived token (same as dashboard)
              this.getLongLiveToken(accessToken).then(res => {
                if (!res.ok) {
                  console.error('[StepConnectPages] getLongLiveToken failed:', res)
                  this.isConnecting = false
                  return
                }

                const longLivedToken = res.body.access_token

                // Helper to add Facebook page and dismiss spinner on first page
                const addFbPage = (page, isFirstCandidate) => {
                  let pageName = page.name
                  // Append city to name for multi-location pages (same as dashboard)
                  if (page.parent_page && page.parent_page.id && page.location && page.location.city) {
                    pageName += ` (${page.location.city})`
                  }

                  const pageToAdd = {
                    id: page.id,
                    network_id: page.id,
                    name: pageName,
                    username: page.id,
                    avatar: page.picture?.data?.url || null,
                    hasUuid: false,
                    rawCredentials: {
                      network: 'facebook',
                      network_id: page.id,
                      name: pageName,
                      avatar: page.picture?.data?.url || null,
                      token: page.access_token
                    }
                  }

                  const autoSelect = isFirstCandidate && !this.hasReachedPageQuota
                  this.addPageIfNotExists('facebook', pageToAdd, autoSelect)
                  // Dismiss spinner immediately as soon as at least 1 page is retrieved
                  this.isConnecting = false
                  this.saveSelectedPages()
                  this.persistOnboardingState()
                }

                // Retrieve pages with pagination and silent retry (to handle Meta token propagation delay)
                const fetchFBPages = async (attempts = 2) => {
                  for (let attempt = 1; attempt <= attempts; attempt++) {
                    try {
                      const res = await this.retrievePages(longLivedToken)
                      if (res && res.ok && res.body && res.body.data) {
                        let allPagesData = [...res.body.data]
                        // Stream first batch into store immediately
                        allPagesData.forEach((page, index) => {
                          const isFirst = index === 0 && (this.connectedPages['facebook'] || []).length === 0
                          addFbPage(page, isFirst)
                        })

                        let nextPagesEndpoint = res.body.paging && res.body.paging.next ? res.body.paging.next : null

                        while (nextPagesEndpoint) {
                          try {
                            const nextPages = await this.$http.get(nextPagesEndpoint)
                            nextPagesEndpoint = nextPages.body && nextPages.body.paging && nextPages.body.paging.next ? nextPages.body.paging.next : null
                            const nextBatch = (nextPages.body && nextPages.body.data) || []
                            nextBatch.forEach(page => {
                              addFbPage(page, false)
                            })
                            allPagesData = allPagesData.concat(nextBatch)
                          } catch (e) {
                            console.error('[StepConnectPages] Pagination error:', e)
                            nextPagesEndpoint = null
                          }
                        }

                        if (allPagesData.length > 0 || attempt === attempts) {
                          return allPagesData
                        }
                      }
                    } catch (err) {
                      console.warn(`[StepConnectPages] retrievePages attempt ${attempt} failed:`, err)
                      if (attempt === attempts) throw err
                    }
                    console.log(`[StepConnectPages] No FB pages found on attempt ${attempt}, retrying in 1200ms...`)
                    await new Promise(resolve => setTimeout(resolve, 1200))
                  }
                  return []
                }

                fetchFBPages(2).then(allPagesData => {
                  const pagesInStore = (this.connectedPages['facebook'] || []).length > 0
                  if (pagesInStore || (allPagesData && allPagesData.length > 0)) {
                    this.$nextTick(() => {
                      this.$forceUpdate()
                    })

                    this.saveSelectedPages()
                    this.persistOnboardingState() // CRITICAL: Persist to localStorage after Facebook connection
                    this.finalizeConnection(this.$t('onboarding.connect_pages.success_facebook'))
                  } else {
                    this.isConnecting = false
                  }
                }).catch(err => {
                  console.error('[StepConnectPages] Error fetching Facebook pages:', err)
                  this.isConnecting = false
                })
              })
            })
          } else {
            this.isConnecting = false
          }
        },
        {
          scope: 'public_profile, pages_show_list, read_insights, pages_manage_posts, pages_manage_engagement, pages_read_user_content, pages_manage_metadata, pages_messaging, business_management, instagram_basic, instagram_content_publish, instagram_manage_insights, instagram_manage_comments, instagram_manage_messages',
          return_scopes: true,
          auth_type: 'rerequest'
        }
      )
    },

    // Instagram connection method (aligned with dashboard flow: long-lived token + pagination + backend APIs)
    connectInsta() {
      if (this.isConnecting) return
      if (this.hasReachedPageQuota) {
        this.notify({
          time: 5000,
          title: this.$t('onboarding.connect_pages.quota_status'),
          text: this.$t('onboarding.connect_pages.quota_banner_reached'),
          color: 'warning'
        })
        return
      }

      this.$ga.event('onboarding', 'connect-instagram')
      this.isConnecting = true
      
      // eslint-disable-next-line
      FB.login(
        response => {
          console.log('[Instagram OAuth] FB.login response:', response)
          
          if (response.status === 'connected') {
            const accessToken = response.authResponse.accessToken

            // Validate token (same as dashboard)
            this.getUserInfoFb(accessToken).then(res => {
              if (!res.ok) {
                console.error('[StepConnectPages] getUserInfoFb failed:', res)
                this.isConnecting = false
                return
              }

              // Exchange for long-lived token (same as dashboard)
              this.getLongLiveToken(accessToken).then(res => {
                if (!res.ok) {
                  console.error('[StepConnectPages] getLongLiveToken failed:', res)
                  this.isConnecting = false
                  return
                }

                const longLivedToken = res.body.access_token

                // Retrieve Instagram accounts with pagination and silent retry (to handle Meta propagation delay)
                const fetchInstagramAccounts = async (attempts = 2) => {
                  for (let attempt = 1; attempt <= attempts; attempt++) {
                    let allPagesFB = []
                    try {
                      const res = await this.retrievePages(longLivedToken)
                      if (res && res.ok && res.body && res.body.data) {
                        allPagesFB = [...res.body.data]
                        let nextPagesEndpoint = res.body.paging && res.body.paging.next ? res.body.paging.next : null
                        while (nextPagesEndpoint) {
                          try {
                            const nextPages = await this.$http.get(nextPagesEndpoint)
                            nextPagesEndpoint = nextPages.body && nextPages.body.paging && nextPages.body.paging.next ? nextPages.body.paging.next : null
                            allPagesFB = allPagesFB.concat((nextPages.body && nextPages.body.data) || [])
                          } catch (e) {
                            console.error('[StepConnectPages] Instagram pagination error:', e)
                            nextPagesEndpoint = null
                          }
                        }
                      }
                    } catch (e) {
                      console.warn(`[StepConnectPages] retrievePages for Instagram attempt ${attempt} error:`, e)
                    }

                    console.log(`[Instagram OAuth] Attempt ${attempt}: Total FB pages after pagination:`, allPagesFB.length)

                    let instaCount = 0
                    const promises = allPagesFB.map(async page => {
                      try {
                        const resInstaPages = await this.retrieveInstaPages(page.id, longLivedToken)
                        if (!resInstaPages.body.instagram_business_account) return null

                        const resInfoInsta = await this.getUserInfoInsta(resInstaPages.body.instagram_business_account.id, longLivedToken)
                        if (!resInfoInsta || !resInfoInsta.body || !resInfoInsta.body.id) return null

                        const pageData = {
                          network: 'instagram',
                          network_id: resInfoInsta.body.id,
                          name: resInfoInsta.body.username,
                          avatar: resInfoInsta.body.profile_picture_url || null,
                          token: longLivedToken,
                          affected_to: page.id
                        }

                        const pageToAdd = {
                          id: resInfoInsta.body.id,
                          network_id: resInfoInsta.body.id,
                          name: resInfoInsta.body.username,
                          username: resInfoInsta.body.username,
                          avatar: resInfoInsta.body.profile_picture_url || null,
                          hasUuid: false,
                          rawCredentials: pageData
                        }

                        const autoSelect = instaCount === 0 && !this.hasReachedPageQuota
                        instaCount++
                        this.addPageIfNotExists('instagram', pageToAdd, autoSelect)
                        // Dismiss spinner immediately as soon as first page is found
                        this.isConnecting = false
                        this.saveSelectedPages()
                        this.persistOnboardingState()
                        this.$nextTick(() => {
                          this.$forceUpdate()
                        })
                        return resInfoInsta.body.id
                      } catch (error) {
                        console.error('[StepConnectPages] Error processing Instagram for FB page:', page.id, error)
                        return null
                      }
                    })

                    const results = await Promise.all(promises)
                    const successCount = results.filter(r => r !== null).length
                    const pagesInStore = (this.connectedPages['instagram'] || []).length > 0

                    if (successCount > 0 || pagesInStore || attempt === attempts) {
                      return { successCount, pagesInStore }
                    }

                    console.log(`[Instagram OAuth] No Instagram accounts found on attempt ${attempt}, waiting 1200ms before retry...`)
                    await new Promise(resolve => setTimeout(resolve, 1200))
                  }
                  return { successCount: 0, pagesInStore: false }
                }

                fetchInstagramAccounts(2).then(({ successCount, pagesInStore }) => {
                  this.$nextTick(() => {
                    this.$forceUpdate()
                  })

                  const pagesInStoreUpdated = (this.connectedPages['instagram'] || []).length > 0

                  if (successCount > 0 || pagesInStoreUpdated) {
                    this.saveSelectedPages()
                    this.persistOnboardingState() // CRITICAL: Persist to localStorage after Instagram connection
                    this.finalizeConnection(this.$t('onboarding.connect_pages.success_instagram'))
                  } else {
                    this.notify({
                      time: 5000,
                      title: this.$t('onboarding.notifications.error_title'),
                      text: this.$t('onboarding.connect_pages.error_instagram_connection'),
                      color: 'danger'
                    })
                    this.isConnecting = false
                  }
                }).catch(error => {
                  console.error('[StepConnectPages] Error in Instagram Promise.all:', error)
                  this.isConnecting = false
                })
              })
            })
          } else {
            this.notify({
              time: 5000,
              title: this.$t('onboarding.notifications.error_title'),
              text: this.$t('onboarding.connect_pages.error_instagram_cancelled'),
              color: 'warning'
            })
            this.isConnecting = false
          }
        },
        {
          scope: 'public_profile, pages_show_list, pages_read_engagement, pages_manage_metadata, read_insights, pages_manage_posts, pages_manage_engagement, pages_read_user_content, business_management, instagram_basic, instagram_content_publish, instagram_manage_insights, instagram_manage_comments, instagram_manage_messages, pages_messaging',
          return_scopes: true,
          auth_type: 'rerequest'
        }
      )
    },

    // Google connection method
    loginWithGoogle() {
      this.$ga.event('onboarding', 'connect-google')
      this.isConnecting = true
      
      this.$gAuth.getAuthCode()
      .then(async authCode => {
        let res = await this.getTokensGoogle(authCode)
        
        if(res && res.body && !res.body.success) {
          this.notify({
            time: 5000,
            title: this.$t('onboarding.notifications.error_title'),
            text: this.$t('onboarding.connect_pages.error_google_connection'),
            color: 'danger'
          })
          this.isConnecting = false
          return
        }
        
        if(res.body.pages && res.body.pages.length > 0) {
          res.body.pages.forEach((page, index) => {
            // Build page data with tokens (same as Pages.vue connectGoogle method)
            const pageData = {
              network: 'google',
              network_id: page.network_id,
              name: page.name,
              avatar: page.avatar
            }
            
            // Add OAuth tokens from Google API response
            if (page.access_token) {
              pageData.token = page.access_token
            }
            
            if (page.refresh_token) {
              pageData.refresh_token = page.refresh_token
            }
            
            if (page.expires_at) {
              // Convert expires_at (in seconds) to ISO date format (same as Pages.vue)
              pageData.expires_at = moment().add(page.expires_at, 'seconds').format()
            }

            const pageToAdd = {
              id: page.network_id,
              network_id: page.network_id,
              name: page.name,
              username: page.network_id,
              avatar: page.avatar,
              hasUuid: false,
              rawCredentials: pageData
            }

            const autoSelect = index === 0 && !this.hasReachedPageQuota
            this.addPageIfNotExists('google', pageToAdd, autoSelect)
            if (index === 0) {
              this.isConnecting = false
            }
          })

          this.$nextTick(() => {
            this.$forceUpdate()
          })

          // Save and persist state
          this.saveSelectedPages()
          this.persistOnboardingState()

          // Check if any page is missing refresh_token (Google doesn't always provide it)
          const missingRefreshToken = res.body.pages.some(page => !page.refresh_token)

          if (missingRefreshToken) {
            console.log('[StepConnectPages] Google pages missing refresh_token — showing refresh modal')
            this.isConnecting = false
            this.googleRefresh.active = true
          } else {
            this.finalizeConnection(this.$t('onboarding.connect_pages.success_google'))
          }
        } else {
          console.log('[StepConnectPages] No Google pages found')
          this.notify({
            time: 5000,
            title: this.$t('onboarding.notifications.info_title'),
            text: this.$t('onboarding.connect_pages.info_no_google_pages'),
            color: 'warning'
          })
          this.isConnecting = false
        }
      }).catch(error => {
        console.error('[StepConnectPages] Google OAuth error:', error)
        this.notify({
          time: 5000,
          title: this.$t('onboarding.notifications.error_title'),
          text: error.message || this.$t('onboarding.connect_pages.error_google_oauth'),
          color: 'danger'
        })
        this.isConnecting = false
      })
    },

    // Bluesky connection method - requires credentials (handle + app password)
    loginWithBluesky() {
      this.$ga.event('onboarding', 'connect-bluesky')
      
      // Open Bluesky auth modal
      this.blueskyAuth.active = true
    },

    // Handle Bluesky connection from modal
    async handleBlueskyConnect({ handle, password }) {
      await this.connectBlueskyWithCredentials(handle, password)
    },

    // Handle Bluesky modal cancel
    handleBlueskyCancel() {
      this.blueskyAuth.active = false
    },

    // Handle Google refresh modal: user chose to reconnect after revoking app access
    handleGoogleReconnect() {
      this.googleRefresh.active = false
      this.loginWithGoogle()
    },

    // Handle Google refresh modal: user chose to skip (token works ~1h, can refresh later from dashboard)
    handleGoogleRefreshSkip() {
      this.googleRefresh.active = false
      this.finalizeConnection(this.$t('onboarding.connect_pages.success_google'))
    },

    // Connect Bluesky with credentials
    async connectBlueskyWithCredentials(identifier, password) {
      this.isConnecting = true
      this.blueskyAuth.active = false
      
      try {
        const res = await this.postLoginBluesky({
          identifier: identifier,
          password: password
        })
        
        
        if (res && res.body && res.body.accessJwt && res.body.refreshJwt && res.body.handle) {
          
          const page = {
            network: 'bluesky',
            token: res.body.accessJwt,
            refresh_token: res.body.refreshJwt,
            name: res.body.handle,
            avatar: null,
            network_id: res.body.did
          }
          
          // Fetch profile to get avatar
          try {
            const profile = await this.getBlueskyProfile(res.body.handle, res.body.accessJwt)
            
            if (profile && profile.body && profile.body.avatar) {
              page.avatar = profile.body.avatar
            }
          } catch (profileErr) {
            console.warn('[StepConnectPages] Could not fetch Bluesky profile, continuing without avatar:', profileErr)
          }
          
          // Save page to backend
          const pageResponse = await this.newPage(page)
          console.log('[StepConnectPages] Bluesky newPage response:', pageResponse)
          
          // Check if response is successful - backend returns { success: true, page: {...} }
          const hasValidUuid = pageResponse && pageResponse.ok && pageResponse.body && pageResponse.body.success && pageResponse.body.page && pageResponse.body.page.id
          const finalId = hasValidUuid ? pageResponse.body.page.id : page.network_id
          const autoSelect = !this.hasReachedPageQuota

          if (hasValidUuid) {
            console.log('[StepConnectPages] Bluesky page created with UUID:', finalId)
          } else {
            console.warn('[StepConnectPages] Backend returned non-UUID response for Bluesky, using network_id:', finalId)
          }

          this.addPageIfNotExists('bluesky', {
            id: finalId,
            network_id: page.network_id,
            name: page.name,
            username: page.name,
            avatar: page.avatar,
            hasUuid: !!hasValidUuid,
            rawCredentials: page
          }, autoSelect)

          // Explicitly guarantee selection of the Bluesky card if quota permits
          if (autoSelect && !this.selectedPageIds.includes(finalId) && this.selectedPageIds.length < this.maxSelectedPages) {
            this.selectedPageIds.push(finalId)
            console.log('[StepConnectPages] Auto-selected Bluesky page in selectedPageIds:', finalId)
          }

          this.$nextTick(() => {
            this.$forceUpdate()
          })

          this.saveSelectedPages()
          this.persistOnboardingState()
          this.finalizeConnection(this.$t('onboarding.connect_pages.success_bluesky'))
        } else {
          console.error('[StepConnectPages] Invalid Bluesky login response:', res)
          this.notify({
            time: 5000,
            title: this.$t('onboarding.notifications.error_title'),
            text: this.$t('onboarding.connect_pages.error_bluesky_invalid_response'),
            color: 'danger'
          })
          this.isConnecting = false
        }
      } catch (error) {
        console.error('[StepConnectPages] Bluesky connection error:', error)
        
        // Reset modal loading state
        if (this.$refs.blueskyModal) {
          this.$refs.blueskyModal.resetLoading()
        }
        
        // Reopen modal with error
        this.blueskyAuth.active = true
        
        this.notify({
          time: 8000,
          title: this.$t('onboarding.notifications.connection_error_title'),
          text: error.body?.message || this.$t('onboarding.connect_pages.error_bluesky_connection'),
          color: 'danger'
        })
        this.isConnecting = false
      }
    },

    togglePageSelection(pageId) {
      console.log('[StepConnectPages] togglePageSelection called with:', pageId)
      
      const page = this.connectedNetworkPages.find(p => p.id === pageId || (p.network_id && p.network_id === pageId))
      const isSelected = this.isPageSelected(pageId)
      
      if (isSelected) {
        // Check if page has active native connection
        const hasNative = page && this.isNativeConnected(page)
        const isPreExisting = this.isPagePreExisting(pageId) || (page && page.network_id && this.isPagePreExisting(page.network_id))

        if (hasNative) {
          this.pageToDeselect = page
          this.deselectModalType = 'native'
          this.showNativeDeselectModal = true
          return
        }

        if (isPreExisting) {
          this.pageToDeselect = page
          this.deselectModalType = 'preExisting'
          this.showNativeDeselectModal = true
          return
        }

        this.executeDeselect(pageId)
      } else {
        // Enforce quota limit before selecting
        if (this.totalSelectedPages >= this.userPlanPageQuota) {
          this.notify({
            time: 5000,
            title: this.$t('onboarding.connect_pages.quota_status'),
            text: this.$t('onboarding.connect_pages.quota_reached'),
            color: 'warning'
          })
          return
        }
        this.selectedPageIds.push(pageId)
        console.log('[StepConnectPages] Page selected, new array:', [...this.selectedPageIds])
        this.saveSelectedPages()
      }
    },

    executeDeselect(pageId) {
      const page = this.connectedNetworkPages.find(p => p.id === pageId || (p.network_id && p.network_id === pageId))
      const networkId = page ? page.network_id : null
      
      let index = this.selectedPageIds.indexOf(pageId)
      if (index === -1 && networkId) {
        index = this.selectedPageIds.indexOf(networkId)
      }
      if (index !== -1) {
        this.selectedPageIds.splice(index, 1)
        console.log('[StepConnectPages] Page deselected, new array:', [...this.selectedPageIds])
      }
      this.saveSelectedPages()
    },

    cancelNativeDeselect() {
      this.showNativeDeselectModal = false
      this.pageToDeselect = null
      this.deselectModalType = null
    },

    confirmNativeDeselect() {
      if (this.pageToDeselect) {
        const pageId = this.pageToDeselect.id
        this.executeDeselect(pageId)
        
        // Remove native connection marker in store if native
        if (this.deselectModalType === 'native' || this.isNativeConnected(this.pageToDeselect)) {
          this.$store.commit('onboarding/SET_NATIVE_CONNECTION', {
            network: this.currentNetwork,
            pageId: pageId,
            connected: false,
            storyConfig: null
          })
          if (this.pageToDeselect.network_id && this.pageToDeselect.network_id !== pageId) {
            this.$store.commit('onboarding/SET_NATIVE_CONNECTION', {
              network: this.currentNetwork,
              pageId: this.pageToDeselect.network_id,
              connected: false,
              storyConfig: null
            })
          }
        }
        this.pageToDeselect = null
      }
      this.showNativeDeselectModal = false
      this.deselectModalType = null
    },

    isNativeConnected(page) {
      if (!page) return false
      const nativeConnections = this.$store.state.onboarding.nativeConnections || {}
      const nativeConnectionStatus = nativeConnections[page.id] || (page.network_id && nativeConnections[page.network_id])
      if (nativeConnectionStatus) {
        return nativeConnectionStatus.connected === true
      }
      return page.story_config && page.story_config.created === true
    },

    isPageSelected(pageId) {
      if (!pageId) return false
      // Find the page object to get its network_id
      const page = this.connectedNetworkPages.find(p => p.id === pageId)
      const networkId = page ? page.network_id : null
      
      return this.selectedPageIds.includes(pageId) ||
        (networkId && this.selectedPageIds.includes(networkId))
    },

    isPagePreExisting(pageId) {
      return this.$store.getters['onboarding/isPagePreExisting'](this.currentNetwork, pageId)
    },

    getPreExistingBadgeText(page) {
      // Check if page has native connection (story_config.created = true)
      const hasNativeConnection = page.story_config && page.story_config.created === true
      
      if (hasNativeConnection) {
        return this.$t('onboarding.connect_pages.badge_previously_connected_native')
      }
      return this.$t('onboarding.connect_pages.badge_previously_connected')
    },

    saveSelectedPages() {
      console.log('[StepConnectPages] Store updated - selectedPageIds for', this.currentNetwork, ':', [...this.selectedPageIds])
      this.$store.commit('onboarding/SET_SELECTED_PAGE_IDS', {
        network: this.currentNetwork,
        pageIds: [...this.selectedPageIds]
      })
    },

    /**
     * Persist complete onboarding state to localStorage
     * CRITICAL: This must be called after EVERY page connection to prevent data loss on refresh
     */
    persistOnboardingState() {
      persistOnboardingPages(this.$store)
      console.log('[StepConnectPages] Onboarding state persisted to localStorage')
    },

    /**
     * Finalize connection with loading delay to prevent race conditions
     * @param {string} successMessage - Message to display on success (can be i18n key or fallback string)
     */
    finalizeConnection(successMessage) {
      // Translate message with fallback to handle missing i18n keys in sandbox
      let translatedMessage = successMessage
      
      // If successMessage looks like an i18n key (contains dots), try to translate it
      if (successMessage && successMessage.includes('.')) {
        const translated = this.$t(successMessage)
        // If translation failed (returns the key itself), use generic fallback
        translatedMessage = translated !== successMessage ? translated : (this.$t('onboarding.connect_pages.success_generic') || 'Page connectée avec succès')
      }
      
      this.notify({
        time: 5000,
        title: this.$t('onboarding.notifications.success_title') || 'Succès',
        text: translatedMessage,
        color: 'success'
      })
      
      // Stop loading spinner
      this.isConnecting = false
    },

    handleImageError(id) {
      this.imageLoadErrors = { ...this.imageLoadErrors, [id]: true }
    },

    /**
     * Add page to store only if it doesn't already exist (check by network_id, id, or name)
     * @param {string} network - Network name (facebook, instagram, etc.)
     * @param {Object} page - Page object with id, network_id, name, etc.
     * @param {boolean} autoSelect - Whether to automatically select the card (default: false)
     * @returns {boolean} - true if page was added, false if it already existed
     */
    addPageIfNotExists(network, page, autoSelect = false) {
      // IMPORTANT: Use this.connectedPages[network] instead of this.connectedNetworkPages
      // to ensure we're checking the correct network, not the currently displayed one
      const existingPages = this.connectedPages[network] || []
      
      // Check if page already exists by:
      // 1. network_id (platform-specific ID like Facebook page ID)
      // 2. id (our backend UUID)
      // 3. name + network (as fallback for cases where IDs differ but it's the same page)
      const pageExists = existingPages.some(p => {
        if (page.network_id && p.network_id === page.network_id) {
          return true
        }
        if (page.id && p.id === page.id) {
          return true
        }
        if (page.name && p.name === page.name) {
          return true
        }
        return false
      })
      
      if (pageExists) {
        console.log(`[StepConnectPages] Page ${page.name} (${page.network_id}) already exists in ${network}, skipping duplicate`)
        const existingPage = existingPages.find(p => 
          (page.network_id && p.network_id === page.network_id) || 
          (page.id && p.id === page.id) ||
          (page.name && p.name === page.name)
        )
        if (autoSelect && existingPage && network === this.currentNetwork && !this.selectedPageIds.includes(existingPage.id)) {
          if (this.selectedPageIds.length < this.maxSelectedPages) {
            this.selectedPageIds.push(existingPage.id)
            console.log(`[StepConnectPages] Auto-selected existing page: ${existingPage.name}`)
          }
        }
        return false
      }
      
      console.log(`[StepConnectPages] Adding page to ${network}: ${page.name} (${page.network_id})`)
      
      if (!page.id && page.network_id) {
        page.id = page.network_id
      }
      
      this.$store.commit('onboarding/ADD_CONNECTED_PAGE', {
        network: network,
        page: page
      })
      
      if (autoSelect && page.id && network === this.currentNetwork && this.selectedPageIds.length < this.maxSelectedPages) {
        this.selectedPageIds.push(page.id)
        console.log(`[StepConnectPages] Auto-selected new page: ${page.name}`)
      }
      
      return true
    },

    async skipStep() {
      if (this.isSavingNetwork) return
      await this.syncCurrentNetworkToBackend()
      
      if (this.currentNetworkIndex < this.selectedNetworks.length - 1) {
        this.currentNetworkIndex++
        this.selectedPageIds = []
        this.loadSelectedPages()
      } else {
        this.$router.push({ name: 'onboarding-summary' })
      }
    },
    
    confirmExitOnboarding() {
      console.log('[StepConnectPages] Exit onboarding confirmed')
      // Reset all onboarding store state (also sets isOnboarding = false)
      this.$store.commit('onboarding/RESET_ONBOARDING')
      // Mark onboarding as skipped to prevent redirect loop
      localStorage.setItem('onboardingSkipped', 'true')
      // Clean up onboarding flags
      this.clearOnboardingFlags()
      // Redirect to dashboard
      this.$router.push({ name: 'dash' })
    },

    clearOnboardingFlags() {
      clearOnboardingFlags()
    },

    async syncCurrentNetworkToBackend() {
      const network = this.currentNetwork
      if (!network) return
      
      const currentPages = this.connectedPages[network] || []
      const selectedIds = this.selectedPageIds || []
      const newlyConnectedIds = this.$store.getters['onboarding/getNewlyConnectedPageIds'](network) || []
      const preExistingIds = this.$store.getters['onboarding/getPreExistingPageIds'](network) || []

      // Normalize hasUuid in store for any page that already has a UUID
      currentPages.forEach(p => {
        if (isUuid(p.id) && !p.hasUuid) {
          this.$store.commit('onboarding/UPDATE_PAGE', {
            network,
            pageId: p.id,
            updates: { hasUuid: true }
          })
        }
      })
      
      // 1. Pages to CREATE in backend: selected, but hasUuid is false AND id is not already a UUID
      const pagesToCreate = currentPages.filter(page => {
        const isSelected = selectedIds.includes(page.id) || (page.network_id && selectedIds.includes(page.network_id))
        const pageHasUuid = page.hasUuid === true || isUuid(page.id)
        return isSelected && !pageHasUuid
      })
      
      // 2. Pages to DELETE from backend: hasUuid is true AND was newly created during onboarding, but is NO LONGER selected
      const pagesToDelete = currentPages.filter(page => {
        const isSelected = selectedIds.includes(page.id) || (page.network_id && selectedIds.includes(page.network_id))
        const pageHasUuid = page.hasUuid === true || isUuid(page.id)
        const isNewlyCreatedInBdd = pageHasUuid && (newlyConnectedIds.includes(page.id) || (page.network_id && newlyConnectedIds.includes(page.network_id)))
        const isPreExisting = preExistingIds.includes(page.id) || (page.network_id && preExistingIds.includes(page.network_id))
        return !isSelected && isNewlyCreatedInBdd && !isPreExisting
      })
      
      if (pagesToCreate.length === 0 && pagesToDelete.length === 0) {
        console.log(`[StepConnectPages] Network ${network} already in sync with backend (nothing to create or delete)`)
        this.saveSelectedPages()
        this.persistOnboardingState()
        return
      }
      
      console.log(`[StepConnectPages] Syncing ${network}: ${pagesToCreate.length} to create, ${pagesToDelete.length} to delete`)
      this.savingNetworkName = this.currentNetworkName
      this.isSavingNetwork = true
      
      try {
        // Create selected pages in BDD in chunks of 5
        for (let i = 0; i < pagesToCreate.length; i += 5) {
          const chunk = pagesToCreate.slice(i, i + 5)
          await Promise.all(chunk.map(async (page) => {
            try {
              const payload = page.rawCredentials || {
                network: page.network || network,
                network_id: page.network_id,
                name: page.name,
                avatar: page.avatar,
                token: page.token
              }
              console.log(`[StepConnectPages] Creating page in BDD: ${page.name}`)
              const res = await this.newPage(payload)
              if (res && res.ok && res.body && res.body.success && res.body.page && res.body.page.id) {
                const createdId = res.body.page.id
                const oldId = page.id
                
                this.$store.commit('onboarding/UPDATE_PAGE', {
                  network,
                  pageId: oldId,
                  updates: {
                    hasUuid: true
                  }
                })

                this.$store.commit('onboarding/REPLACE_PAGE_ID', {
                  network,
                  oldPageId: oldId,
                  newPageId: createdId
                })
                
                const idIdx = this.selectedPageIds.indexOf(oldId)
                if (idIdx !== -1) {
                  this.selectedPageIds.splice(idIdx, 1, createdId)
                } else if (!this.selectedPageIds.includes(createdId)) {
                  this.selectedPageIds.push(createdId)
                }
                
                this.$store.commit('onboarding/TRACK_NEWLY_CONNECTED_ID', {
                  network,
                  pageId: createdId
                })
              } else if (res && (res.status === 422 || (res.body && res.body.error && res.body.error.includes('already_exist')))) {
                // Page already exists in backend BDD
                console.log(`[StepConnectPages] Page ${page.name} already exists in BDD, marking hasUuid: true`)
                this.$store.commit('onboarding/UPDATE_PAGE', {
                  network,
                  pageId: page.id,
                  updates: {
                    hasUuid: true
                  }
                })
              }
            } catch (err) {
              console.error(`[StepConnectPages] Error creating page ${page.name} in BDD:`, err)
            }
          }))
        }
        
        // Delete unselected pages from BDD in chunks of 5
        for (let i = 0; i < pagesToDelete.length; i += 5) {
          const chunk = pagesToDelete.slice(i, i + 5)
          await Promise.all(chunk.map(async (page) => {
            try {
              console.log(`[StepConnectPages] Deleting unselected page from BDD: ${page.name} (${page.id})`)
              await this.deletePage(page.id)
              
              const originalId = page.network_id || page.id
              this.$store.commit('onboarding/UPDATE_PAGE', {
                network,
                pageId: page.id,
                updates: {
                  hasUuid: false
                }
              })

              if (page.id !== originalId) {
                this.$store.commit('onboarding/REPLACE_PAGE_ID', {
                  network,
                  oldPageId: page.id,
                  newPageId: originalId
                })
              }
              
              this.$store.commit('onboarding/UNTRACK_NEWLY_CONNECTED_ID', {
                network,
                pageId: page.id
              })
            } catch (err) {
              console.error(`[StepConnectPages] Error deleting page ${page.name}:`, err)
            }
          }))
        }
        
        this.saveSelectedPages()
        this.persistOnboardingState()
      } finally {
        this.isSavingNetwork = false
      }
    },

    async goToNextStep() {
      if (this.isSavingNetwork) return
      
      if (this.viewMode === 'api') {
        // Save & sync selections of current network to backend
        await this.syncCurrentNetworkToBackend()
        
        // If native feature is enabled for this network and network supports native, switch to native view.
        // For Facebook, Instagram, LinkedIn and X/Twitter (which support direct native connection),
        // switch to native view even if no pages were selected/connected in API view.
        if (this.isCurrentNetworkNativeFeatureSelected && this.supportsExtensionNative(this.currentNetwork)) {
          const currentSelected = this.selectedPageIds || []
          if (currentSelected.length > 0 || this.supportsDirectNativeAdd) {
            console.log(`[StepConnectPages] Switching to native view for ${this.currentNetwork}`)
            this.viewMode = 'native'
            return
          }
        }
      }
      
      // Moving to next network or summary: reset viewMode to api
      this.viewMode = 'api'
      
      if (this.currentNetworkIndex < this.selectedNetworks.length - 1) {
        this.currentNetworkIndex++
        this.selectedPageIds = []
      } else {
        console.log('[StepConnectPages] All networks completed - redirecting to summary')
        this.$router.push({ name: 'onboarding-summary' })
      }
    },

    backToApiView() {
      console.log('[StepConnectPages] Returning to API view from native view')
      this.viewMode = 'api'
    },

    async handleNetworkSelected(network) {
      if (this.isSavingNetwork) return
      
      // Sync selections of current network to backend before switching
      await this.syncCurrentNetworkToBackend()
      
      const index = this.selectedNetworks.indexOf(network)
      if (index !== -1) {
        this.viewMode = 'api'
        this.currentNetworkIndex = index
      }
    },

    loadSelectedPages() {
      const savedPageIds = this.$store.state.onboarding.selectedPageIds[this.currentNetwork]
      console.log('[StepConnectPages] Loading selected pages for', this.currentNetwork, ':', savedPageIds)
      if (savedPageIds && Array.isArray(savedPageIds)) {
        this.selectedPageIds = [...savedPageIds]
      } else {
        this.selectedPageIds = []
      }
    },

    async loadExistingPages() {
      console.log('[StepConnectPages] Loading existing pages from Dashboard...')
      
      try {
        const response = await this.getPages()
        
        if (response.ok && response.body && response.body.pages) {
          const allPages = response.body.pages
          console.log('[StepConnectPages] Total pages from backend:', allPages.length)
          
          // Filter and add pages only for selected networks
          this.selectedNetworks.forEach(network => {
            const networkPages = allPages.filter(p => p.network === network)
            console.log(`[StepConnectPages] Found ${networkPages.length} existing pages for ${network}`)
            
            // Track pre-existing page IDs (pages that existed BEFORE onboarding)
            const preExistingIds = []
            const newlyConnectedIds = this.$store.getters['onboarding/getNewlyConnectedPageIds'](network) || []
            const storePreExistingIds = this.$store.state.onboarding.preExistingPageIds[network] || []
            
            networkPages.forEach(page => {
              // Check if page is not already in store
              const existingPages = this.connectedPages[network] || []
              const alreadyExists = existingPages.some(p => p.id === page.id)
              
              // Check if page was pre-existing:
              // Any page found in BDD that was not created during this current session is pre-existing
              const isNewlyCreatedInSession = newlyConnectedIds.includes(page.id) ||
                (page.network_id && newlyConnectedIds.includes(page.network_id))
              const wasPreExisting = (!isNewlyCreatedInSession) ||
                storePreExistingIds.includes(page.id) ||
                (page.network_id && storePreExistingIds.includes(page.network_id)) ||
                page.preExisting === true

              if (wasPreExisting) {
                preExistingIds.push(page.id)
              }

              if (!alreadyExists) {
                console.log(`[StepConnectPages] Adding existing page to store: ${page.name} (${page.id})`)
                
                // Check if page has native connection
                const hasNativeConnection = page.story_config && page.story_config.created === true
                if (hasNativeConnection) {
                  console.log(`[StepConnectPages] Page ${page.name} already has native connection (story_config.created = true)`)
                  this.$store.commit('onboarding/SET_NATIVE_CONNECTION', {
                    network,
                    pageId: page.id,
                    connected: true,
                    storyConfig: page.story_config
                  })
                  if (page.network_id && page.network_id !== page.id) {
                    this.$store.commit('onboarding/SET_NATIVE_CONNECTION', {
                      network,
                      pageId: page.network_id,
                      connected: true,
                      storyConfig: page.story_config
                    })
                  }
                }
                
                // Use addPageIfNotExists to prevent duplicates (with autoSelect = true)
                const wasAdded = this.addPageIfNotExists(network, {
                  id: page.id,
                  network_id: page.network_id,
                  name: page.name,
                  username: page.username || page.name,
                  avatar: page.avatar,
                  story_config: page.story_config || null, // Include story_config from dashboard
                  hasUuid: true,
                  preExisting: wasPreExisting // Flag to indicate this page existed before onboarding
                }, true)
                
                if (wasAdded) {
                  console.log(`[StepConnectPages] Auto-selected existing page: ${page.name}`)
                }
              }
            })
             
             // Mark all these pages as pre-existing in the store
             if (preExistingIds.length > 0) {
               this.$store.commit('onboarding/SET_PRE_EXISTING_PAGE_IDS', {
                 network: network,
                 pageIds: preExistingIds
               })
               
               // Merge pre-existing IDs into selectedPageIds ONLY if it hasn't been initialized yet
               // (first visit of the network during onboarding).
               // If it was already visited (i.e. selectedPageIds[network] is an array), we keep
               // the user's manual selections/deselections and do not re-merge pre-existing pages.
               const currentSelected = this.$store.state.onboarding.selectedPageIds[network]
               if (currentSelected === undefined) {
                 this.$store.commit('onboarding/SET_SELECTED_PAGE_IDS', {
                   network: network,
                   pageIds: preExistingIds
                 })
                 console.log(`[StepConnectPages] First visit: Auto-selected ${preExistingIds.length} pre-existing pages for ${network}`)
               } else {
                 console.log(`[StepConnectPages] Subsequent visit: Keeping current selections for ${network}:`, currentSelected)
               }
             }
          })
          
          console.log('[StepConnectPages] Finished loading existing pages')
          console.log('[StepConnectPages] Pre-existing pages:', this.$store.state.onboarding.preExistingPageIds)
          console.log('[StepConnectPages] Auto-selected pages:', this.$store.state.onboarding.selectedPageIds)
        }
      } catch (error) {
        console.error('[StepConnectPages] Error loading existing pages:', error)
      }
    },

    restoreOnboardingState() {
      // Restore state after an OAuth return
      const isOnboardingMode = localStorage.getItem('onboarding_mode')
      const savedNetwork = localStorage.getItem('onboarding_current_network')
      const savedIndex = localStorage.getItem('onboarding_current_index')
      const savedNetworks = localStorage.getItem('onboarding_selected_networks')
      const savedFeatures = localStorage.getItem('onboarding_selected_features')
      
      console.log('[StepConnectPages] localStorage check:', {
        onboarding_mode: isOnboardingMode,
        saved_network: savedNetwork,
        saved_index: savedIndex,
        saved_networks: savedNetworks,
        saved_features: savedFeatures
      })
      
      if (isOnboardingMode === 'true') {
        // Restore Vuex store data that was lost on page reload
        if (savedNetworks) {
          const networks = JSON.parse(savedNetworks)
          console.log('[StepConnectPages] Restoring selectedNetworks to store:', networks)
          this.$store.commit('onboarding/SET_NETWORKS', networks)
        }
        
        if (savedFeatures) {
          const features = JSON.parse(savedFeatures)
          console.log('[StepConnectPages] Restoring selectedFeatures to store:', features)
          this.$store.commit('onboarding/SET_FEATURES', features)
        }
        
        // Restore page connection data (connectedPages, selectedPageIds, preExistingPageIds, etc.)
        const pagesRestored = restoreOnboardingPages(this.$store)
        console.log('[StepConnectPages] Page connection data restored:', pagesRestored)
        
        // Wait for next tick to ensure computed properties are updated
        this.$nextTick(() => {
          // Restore network index if saved, BUT ONLY if we didn't receive an explicit network or networkIndex from route params
          // (to avoid overwriting the correct navigation from StepConnectExtensions/StepConnectNatives/Summary)
          const hasExplicitNetworkIndex = this.$route.params.networkIndex !== undefined
          const hasExplicitNetwork = !!this.$route.params.network
          if (savedIndex && !hasExplicitNetworkIndex && !hasExplicitNetwork) {
            this.currentNetworkIndex = parseInt(savedIndex)
            console.log('[StepConnectPages] Restored to network:', this.selectedNetworks[this.currentNetworkIndex])
          } else if (hasExplicitNetworkIndex) {
            console.log('[StepConnectPages] Skipping localStorage restore - using explicit networkIndex from route')
          }
          
          // Refresh pre-existing pages from backend to ensure we have fresh data
          // This is critical after OAuth redirect since restored pages from localStorage may be stale
          this.loadExistingPages().then(() => {
            this.loadSelectedPages()
            this.handleOAuthRedirects()
          })
        })
        
        // DON'T clear flags here - they must persist for multiple OAuth connections
        // Flags will be cleared when user exits onboarding (via Terminer or back to dashboard)
        console.log('[StepConnectPages] \u2705 State restored from localStorage, keeping flags for next OAuth if needed')
      }
    },

    handleOAuthRedirects() {
      if (this.oauthCallbackProcessed) return
      
      const hashParts = location.hash.split('?')
      const queryString = hashParts[1] || ''
      const params = new URLSearchParams(queryString)
      
      const oauth_token = params.get('oauth_token')
      const oauth_verifier = params.get('oauth_verifier')
      const code = params.get('code')
      const state = params.get('state')

      const showSuccess = (message) => {
        this.notify({
          time: 5000,
          title: this.$t('onboarding.notifications.success_title') || 'Succès',
          text: message || this.$t('onboarding.connect_pages.oauth_success_default') || 'Connexion réussie',
          color: 'success',
          id: 'norepeat'
        })
      }

      const showFailed = (error) => {
        let title = this.$t('onboarding.notifications.error_title') || 'Erreur'
        let text = error || this.$t('onboarding.connect_pages.oauth_error_default') || 'Erreur de connexion'

        // If error is a backend error key (e.g. "dash.error.page_already_exist_other_account"),
        // use its .title / .body translation keys instead of displaying the raw key string.
        if (error && this.$te(`${error}.title`) && this.$te(`${error}.body`)) {
          title = this.$t(`${error}.title`)
          text = this.$t(`${error}.body`)
        }

        this.notify({
          time: 10000,
          title,
          text,
          color: 'danger',
          id: 'norepeat'
        })
      }

      // Twitter OAuth 1.0 callback
      if (oauth_token && oauth_verifier) {
        this.handleOAuth1Callback({ oauth_token, oauth_verifier, showSuccess, showFailed })
        return
      }
      
      // OAuth 2.0 callback
      if (code) {
        this.handleOAuth2Callback({ code, state, showSuccess, showFailed })
        return
      }
      
      this.oauthCallbackProcessed = true
    },

    // Generic OAuth 1.0 handler
    handleOAuth1Callback({ oauth_token, oauth_verifier, showSuccess, showFailed }) {
      console.log('[StepConnectPages] Processing OAuth 1.0 callback (Twitter)')
      
      // Activate loading spinner on OAuth return
      this.isConnecting = true
      
      this.addTwitterV1({
        oauth_token,
        oauth_verifier,
        refresh_page: null
      })
      .then((res) => {
        console.log('[StepConnectPages] Twitter API response:', res)
        const hasValidUuid = res && res.ok && res.body && res.body.page && res.body.page.id
        
        if (hasValidUuid) {
          this.addPageIfNotExists('x', {
            id: res.body.page.id,
            network_id: res.body.page.network_id,
            name: res.body.page.name,
            username: res.body.page.name,
            avatar: res.body.page.avatar,
            hasUuid: true
          })
          
          this.$nextTick(() => {
            this.$forceUpdate()
          })
        } else {
          console.warn('[StepConnectPages] Backend returned invalid response for Twitter, using fallback')
          console.error('[StepConnectPages] Response details:', {
            ok: res && res.ok,
            status: res && res.status,
            hasBody: res && !!res.body,
            hasPage: res && res.body && !!res.body.page
          })
          
          // Fallback: Try to extract network_id from response or use screen_name
          const fallbackId = (res && res.body && res.body.page && res.body.page.network_id) || 'twitter_user'
          const fallbackName = (res && res.body && res.body.page && res.body.page.name) || 'Twitter Account'
          
          this.addPageIfNotExists('x', {
            id: fallbackId,
            network_id: fallbackId,
            name: fallbackName,
            username: fallbackName,
            avatar: (res && res.body && res.body.page && res.body.page.avatar) || null,
            hasUuid: false
          })
          
          this.$nextTick(() => {
            this.$forceUpdate()
          })
        }
        
        history.replaceState({}, document.title, location.pathname + location.hash)
        this.saveSelectedPages()
        this.finalizeConnection(this.$t(this.oauthProviders.x.successMessage))
        this.oauthCallbackProcessed = true
      })
      .catch(e => {
        console.error('[StepConnectPages] Twitter connection error:', e)
        showFailed(e.body?.error || this.$t('onboarding.connect_pages.error_twitter_connection'))
        this.isConnecting = false
        this.oauthCallbackProcessed = true
      })
    },

    /**
     * Fetch pages from backend with silent retry to handle DB replication latency
     * @param {string} networkKey - The network identifier (youtube, tiktok, threads, twitter)
     * @param {number} maxRetries - Maximum number of attempts (default 2)
     * @param {number} delayMs - Delay in ms between retries (default 1200ms)
     * @returns {Promise<Array>}
     */
    async fetchPagesWithRetry(networkKey, maxRetries = 2, delayMs = 1200) {
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const pagesRes = await this.getPages()
          const allPages = (pagesRes && pagesRes.body && pagesRes.body.pages) || []
          const matched = allPages.filter(p => p.network === networkKey)
          if (matched.length > 0) {
            return matched
          }
        } catch (e) {
          console.warn(`[StepConnectPages] Error fetching ${networkKey} pages on attempt ${attempt}:`, e)
        }
        if (attempt < maxRetries) {
          console.log(`[StepConnectPages] No ${networkKey} pages found on attempt ${attempt}, waiting ${delayMs}ms for DB sync...`)
          await new Promise(resolve => setTimeout(resolve, delayMs))
        }
      }
      return []
    },

    // Generic OAuth 2.0 handler with provider detection
    handleOAuth2Callback({ code, state, showSuccess, showFailed }) {
      this.isConnecting = true
      
      const provider = this.detectOAuth2Provider(state)
      
      if (!provider) {
        showFailed(this.$t('onboarding.connect_pages.error_oauth_provider_unknown'))
        this.isConnecting = false
        return
      }
      
      const verifyParams = provider.requiresState ? { code, state } : { code }
      let promiseChain
      
      // Special handling for Twitter OAuth 2.0 which uses newPage directly
      if (provider.usesNewPageDirectly) {
        console.log('[StepConnectPages] Twitter OAuth 2.0 - calling newPage directly with code')
        promiseChain = this.newPage({
          network: 'twitter',
          code: code
        }).then(async (newPageRes) => {
          console.log('[StepConnectPages] Twitter newPage response:', newPageRes)
          
          // Check if response is successful - backend returns { success: true, page: {...} }
          if (newPageRes && newPageRes.ok && newPageRes.body && newPageRes.body.success) {
            console.log('[StepConnectPages] Twitter page created successfully, fetching with silent retry')
            const twitterPages = await this.fetchPagesWithRetry('twitter', 2, 1200)
            console.log('[StepConnectPages] Found', twitterPages.length, 'Twitter accounts')
            
            if (twitterPages.length > 0) {
              return {
                body: {
                  pages: twitterPages
                }
              }
            } else {
              throw new Error(this.$t('onboarding.connect_pages.error_twitter_not_found'))
            }
          } else {
            console.error('[StepConnectPages] Invalid Twitter newPage response:', newPageRes)
            throw new Error(this.$t('onboarding.connect_pages.error_twitter_create_failed'))
          }
        })
      } else {
        promiseChain = this[provider.verifyMethod](verifyParams)
      }
      
      // For LinkedIn, add an extra step to fetch organizations with silent retry
      if (provider.networkKey === 'linkedin') {
        promiseChain = promiseChain
          .then(async () => {
            console.log('[StepConnectPages] LinkedIn verified, fetching organizations with silent retry...')
            let body = null
            for (let attempt = 1; attempt <= 2; attempt++) {
              try {
                const orgsRes = await this.getLinkedInOrganizations()
                if (orgsRes && orgsRes.body && Array.isArray(orgsRes.body.elements) && orgsRes.body.elements.length > 0) {
                  body = orgsRes.body
                  break
                }
                body = (orgsRes && orgsRes.body) || {}
              } catch (err) {
                console.warn(`[StepConnectPages] LinkedIn orgs attempt ${attempt} failed:`, err)
                if (attempt === 2) throw err
              }
              if (attempt < 2) {
                console.log('[StepConnectPages] LinkedIn organizations empty or delayed, retrying in 1200ms...')
                await new Promise(resolve => setTimeout(resolve, 1200))
              }
            }
            return { body: body || {} }
          })
          .then(({ body }) => {
            console.log('[StepConnectPages] LinkedIn organizations response:', body)
            const { elements = [] } = body
            
            // Transform LinkedIn data to standard format
            const linkedinPages = elements.map(e => ({
              network_id: e['organizationalTarget~'].id,
              name: e['organizationalTarget~'].localizedName,
              avatar: e['organizationalTarget~'].logoV2?.['original~']?.elements?.[1]?.identifiers?.[0]?.identifier || null,
              username: e['organizationalTarget~'].localizedName // Use name as username
            }))
            
            // Do NOT save LinkedIn pages to backend yet - keep as candidates
            return {
              body: {
                pages: linkedinPages.map(page => ({
                  id: page.network_id,
                  network_id: page.network_id,
                  name: page.name,
                  avatar: page.avatar,
                  username: page.username,
                  hasUuid: false,
                  rawCredentials: {
                    network: 'linkedin',
                    network_id: page.network_id,
                    name: page.name,
                    avatar: page.avatar
                  }
                }))
              }
            }
          })
      }
      
      // For Pinterest, user account becomes the "page"
      if (provider.networkKey === 'pinterest') {
        promiseChain = promiseChain
          .then((res) => {
            console.log('[StepConnectPages] Pinterest verified, preparing candidate page...')
            console.log('[StepConnectPages] Pinterest response:', res.body)
            
            const { user, token: oauth_token } = res.body
            
            // Build token object as expected by backend
            const token = {
              content: oauth_token.access_token,
              network: 'pinterest',
              expires_at: oauth_token.expires_in,
              network_id: user.username,
              refresh_token: oauth_token.refresh_token,
              refresh_token_expires_at: oauth_token.refresh_token_expires_in
            }
            
            return {
              body: {
                pages: [{
                  id: user.username,
                  network_id: user.username,
                  name: user.username,
                  avatar: user.profile_image || null,
                  username: user.username,
                  hasUuid: false,
                  rawCredentials: {
                    network: 'pinterest',
                    token,
                    name: user.username,
                    avatar: user.profile_image || null,
                    network_id: user.username
                  }
                }]
              }
            }
          })
      }
      
      promiseChain
        .then(res => {
          console.log(`[StepConnectPages] ${provider.name} API response:`, res)
          console.log(`[StepConnectPages] ${provider.name} res.body:`, res.body)
          console.log(`[StepConnectPages] ${provider.name} res.body.pages:`, res.body?.pages)
          
          // Special handling for YouTube, TikTok, Threads - backend creates resources in DB
          if (provider.networkKey === 'youtube') {
            console.log('[StepConnectPages] YouTube special handling - fetching channels with silent retry')
            return this.fetchPagesWithRetry('youtube', 2, 1200).then(youtubeChannels => {
              console.log('[StepConnectPages] Found', youtubeChannels.length, 'YouTube channels')
              if (youtubeChannels.length > 0) {
                return {
                  body: {
                    pages: youtubeChannels
                  }
                }
              } else {
                throw new Error(this.$t('onboarding.connect_pages.error_youtube_not_found'))
              }
            })
          }
          
          if (provider.networkKey === 'tiktok') {
            console.log('[StepConnectPages] TikTok special handling - fetching accounts with silent retry')
            return this.fetchPagesWithRetry('tiktok', 2, 1200).then(tiktokAccounts => {
              console.log('[StepConnectPages] Found', tiktokAccounts.length, 'TikTok accounts')
              if (tiktokAccounts.length > 0) {
                return {
                  body: {
                    pages: tiktokAccounts
                  }
                }
              } else {
                throw new Error(this.$t('onboarding.connect_pages.error_tiktok_not_found'))
              }
            })
          }
          
          if (provider.networkKey === 'threads') {
            console.log('[StepConnectPages] Threads special handling - fetching accounts with silent retry')
            return this.fetchPagesWithRetry('threads', 2, 1200).then(threadsAccounts => {
              console.log('[StepConnectPages] Found', threadsAccounts.length, 'Threads accounts')
              if (threadsAccounts.length > 0) {
                return {
                  body: {
                    pages: threadsAccounts
                  }
                }
              } else {
                throw new Error(this.$t('onboarding.connect_pages.error_threads_not_found'))
              }
            })
          }
          
          return res
        })
        .then(res => {
          if (res.body && res.body.pages && res.body.pages.length > 0) {
            console.log(`[StepConnectPages] Found ${res.body.pages.length} ${provider.name} pages`)
            
            res.body.pages.forEach((page, index) => {
              console.log(`[StepConnectPages] Processing ${provider.name} page ${index + 1}:`, page)
              console.log(`[StepConnectPages] Page structure:`, {
                network_id: page.network_id,
                name: page.name,
                avatar: page.avatar,
                full_page: page
              })
              
              const pageHasUuid = page.hasUuid === true || isUuid(page.id) || ['youtube', 'tiktok', 'threads'].includes(provider.networkKey)
              const pageToAdd = {
                id: page.id,
                network_id: page.network_id,
                name: page.name,
                username: page.username || page.name || page.network_id, // Use real username from backend
                avatar: page.avatar,
                hasUuid: pageHasUuid,
                rawCredentials: page.rawCredentials || null
              }
              
              const isMultiPage = ['facebook', 'instagram', 'linkedin', 'google'].includes(provider.networkKey)
              const autoSelect = isMultiPage
                ? (index === 0 && !this.hasReachedPageQuota)
                : !this.hasReachedPageQuota

              this.addPageIfNotExists(provider.networkKey, pageToAdd, autoSelect)
              if (index === 0) {
                this.isConnecting = false
              }
              if (pageHasUuid && page.id) {
                this.$store.commit('onboarding/TRACK_NEWLY_CONNECTED_ID', {
                  network: provider.networkKey,
                  pageId: page.id
                })
              }
              
              console.log(`[StepConnectPages] Store AFTER commit:`, JSON.parse(JSON.stringify(this.$store.state.onboarding.connectedPages)))
              console.log(`[StepConnectPages] connectedPages[${provider.networkKey}]:`, this.$store.state.onboarding.connectedPages[provider.networkKey])
            })
            
            console.log(`[StepConnectPages] Before $nextTick - currentNetwork:`, this.currentNetwork)
            console.log(`[StepConnectPages] Before $nextTick - connectedNetworkPages:`, this.connectedNetworkPages)
            
            // Force Vue to update
            this.$nextTick(() => {
              console.log('[StepConnectPages] After $nextTick - currentNetwork:', this.currentNetwork)
              console.log('[StepConnectPages] After $nextTick - connectedNetworkPages:', this.connectedNetworkPages)
              console.log('[StepConnectPages] After $nextTick - store connectedPages:', this.$store.state.onboarding.connectedPages)
              this.$forceUpdate()
              
              // Double check after forceUpdate
              this.$nextTick(() => {
                console.log('[StepConnectPages] After $forceUpdate - connectedNetworkPages:', this.connectedNetworkPages)
              })
            })
            
            history.replaceState({}, document.title, location.pathname + location.hash)
            this.saveSelectedPages()
            this.persistOnboardingState() // CRITICAL: Persist to localStorage after OAuth
            this.finalizeConnection(this.$t(provider.successMessage))
            this.oauthCallbackProcessed = true
          } else {
            console.log(`[StepConnectPages] No pages in ${provider.name} response`)
            console.log(`[StepConnectPages] Full response body:`, JSON.stringify(res.body, null, 2))
            console.warn(`[StepConnectPages] Backend did not return pages array for ${provider.name}`)
            console.warn('[StepConnectPages] This might indicate:')
            console.warn('  1. User has no organization pages with admin access')
            console.warn('  2. Backend failed to fetch organization pages from LinkedIn API')
            console.warn('  3. LinkedIn app scopes are insufficient or not approved')
            history.replaceState({}, document.title, location.pathname + location.hash)
            
            // More helpful error message for LinkedIn
            const errorMessage = provider.networkKey === 'linkedin'
              ? this.$t('onboarding.connect_pages.error_linkedin_no_page')
              : this.$t('onboarding.connect_pages.error_no_pages_found', { provider: provider.name })
            
            showFailed(errorMessage)
            this.isConnecting = false
            this.oauthCallbackProcessed = true
          }
        })
        .catch(e => {
          console.error(`[StepConnectPages] ${provider.name} connection error:`, e)
          console.error(`[StepConnectPages] ${provider.name} error details:`, e.body)
          showFailed(e.body?.error || this.$t('onboarding.connect_pages.error_connection_failed', { provider: provider.name }))
          this.isConnecting = false
          this.oauthCallbackProcessed = true
        })
    },

    // Detect OAuth 2.0 provider based on currentNetwork or state
    detectOAuth2Provider(state) {
      // Priority 1: Check by state parameter (Threads, Instagram Business)
      for (const [key, config] of Object.entries(this.oauthProviders)) {
        if (config.detectBy === 'state' && config.stateValue === state) {
          return {
            name: key.charAt(0).toUpperCase() + key.slice(1),
            networkKey: key,
            ...config
          }
        }
      }
      
      // Priority 2: Check by currentNetwork (restored from localStorage)
      // Fallback to localStorage if Vuex store lost the network during redirect
      const resolvedNetwork = this.currentNetwork || localStorage.getItem('onboarding_current_network')
      
      if (resolvedNetwork && this.oauthProviders[resolvedNetwork]) {
        const config = this.oauthProviders[resolvedNetwork]
        if (config.detectBy === 'currentNetwork') {
          console.log('[StepConnectPages] Detected OAuth provider by currentNetwork:', resolvedNetwork,
            resolvedNetwork !== this.currentNetwork ? '(from localStorage fallback)' : '')
          return {
            name: resolvedNetwork.charAt(0).toUpperCase() + resolvedNetwork.slice(1),
            networkKey: resolvedNetwork,
            ...config
          }
        }
      }
      
      console.warn('[StepConnectPages] Could not detect OAuth 2.0 provider. State:', state, 'currentNetwork:', this.currentNetwork, 'localStorage fallback:', localStorage.getItem('onboarding_current_network'))
      return null
    }
  },
  async mounted() {
    console.log('=== [StepConnectPages] MOUNTED ===')
    window.addEventListener('pageshow', this.handlePageShow)
    
    // HANDLE OAUTH PARAMS BEFORE HASH (LinkedIn, Facebook sometimes redirect like: ?code=XXX#/path)
    const searchParams = new URLSearchParams(window.location.search)
    const hasOAuthParamsBeforeHash = searchParams.has('code') || searchParams.has('oauth_token')
    
    if (hasOAuthParamsBeforeHash && !window.location.hash.includes('?')) {
      console.log('[StepConnectPages] OAuth params detected BEFORE hash - moving them after hash')
      console.log('[StepConnectPages] Current URL:', window.location.href)
      
      // Move params from ?code=XXX#/path to #/path?code=XXX
      const currentHash = window.location.hash.replace('#', '')
      const newUrl = `${window.location.origin}${window.location.pathname}#${currentHash}${window.location.search}`
      
      console.log('[StepConnectPages] Redirecting to:', newUrl)
      window.location.href = newUrl
      return
    }
    
    console.log('[StepConnectPages] selectedNetworks:', this.selectedNetworks)
    console.log('[StepConnectPages] currentNetwork:', this.currentNetwork)
    console.log('[StepConnectPages] currentNetworkName:', this.currentNetworkName)
    console.log('[StepConnectPages] Store state:', {
      connectedPages: this.$store.state.onboarding.connectedPages,
      selectedPageIds: this.$store.state.onboarding.selectedPageIds,
      selectedFeatures: this.$store.state.onboarding.selectedFeatures
    })
    console.log('[StepConnectPages] LocalStorage:', {
      onboarding_mode: localStorage.getItem('onboarding_mode'),
      current_network: localStorage.getItem('onboarding_current_network'),
      current_index: localStorage.getItem('onboarding_current_index')
    })
    console.log('[StepConnectPages] URL params:', {
      search: location.search,
      hash: location.hash,
      href: location.href
    })

    if (this.selectedNetworks.length === 0) {
      // Check if we're in an OAuth return (onboarding_mode flag present)
      const isOAuthReturn = localStorage.getItem('onboarding_mode') === 'true'
      
      if (isOAuthReturn) {
        console.log('[StepConnectPages] No networks but onboarding_mode detected - OAuth return, restoring state first')
        // Don't redirect yet, let restoreOnboardingState() handle it
      } else {
        // Check if we're in a plain page reload (parent Onboarding.vue will restore Vuex shortly
        // in its own mounted(), which fires after child components due to Vue 2 lifecycle order)
        const hasSavedNetworks = (() => {
          try {
            const str = localStorage.getItem('onboarding_state')
            return str ? ((JSON.parse(str).selectedNetworks?.length || 0) > 0) : false
          } catch (e) {
            console.error('[StepConnectPages] Failed to parse onboarding_state:', e)
            return false
          }
        })()

        if (hasSavedNetworks) {
          console.log('[StepConnectPages] Saved state found - deferring init to selectedNetworks watcher (reload scenario)')
          this._deferredInit = true
          return
        }

        console.log('[StepConnectPages] No networks selected, redirecting to networks step')
        this.$router.push({ name: 'onboarding-networks' })
        return
      }
    }
    
    // Check if returning from native connections (via route params)
    if (this.$route.params.fromNatives === true) {
      console.log('[StepConnectPages] Returning from native connections')
      console.log('[StepConnectPages] Current network index:', this.currentNetworkIndex)
      
      // Move to next network
      if (this.currentNetworkIndex < this.selectedNetworks.length - 1) {
        this.currentNetworkIndex++
        this.selectedPageIds = []
        console.log('[StepConnectPages] Moved to next network:', this.selectedNetworks[this.currentNetworkIndex])
      } else {
        console.log('[StepConnectPages] All networks completed, redirecting to summary')
        this.$router.push({ name: 'onboarding-summary' })
        return
      }
    }
    
    // Check if coming from StepConnectNatives or StepConnectExtensions with specific network index
    const hasExplicitNetworkIndex = this.$route.params.networkIndex !== undefined
    if (hasExplicitNetworkIndex) {
      const networkIndex = parseInt(this.$route.params.networkIndex)
      console.log('[StepConnectPages] Coming from StepConnectNatives/Extensions with network index:', networkIndex)
      
      if (networkIndex >= 0 && networkIndex < this.selectedNetworks.length) {
        this.currentNetworkIndex = networkIndex
        this.selectedPageIds = []
        console.log('[StepConnectPages] Set network index to:', networkIndex, '- Network:', this.selectedNetworks[networkIndex])
      } else {
        console.warn('[StepConnectPages] Invalid network index:', networkIndex)
      }
    }
    
    // Check if coming from summary with specific network
    if (this.$route.params.network) {
      const targetNetwork = this.$route.params.network
      console.log('[StepConnectPages] Coming from summary for network:', targetNetwork)
      
      let networkIndex = this.selectedNetworks.indexOf(targetNetwork)
      if (networkIndex === -1) {
        if (targetNetwork === 'twitter') networkIndex = this.selectedNetworks.indexOf('x')
        else if (targetNetwork === 'x') networkIndex = this.selectedNetworks.indexOf('twitter')
      }
      if (networkIndex !== -1) {
        this.currentNetworkIndex = networkIndex
        console.log('[StepConnectPages] Set network index to:', networkIndex)
      } else {
        console.warn('[StepConnectPages] Network not found in selected networks:', targetNetwork)
      }
    }

    // Check if returning from StepConnectExtensions or coming from StepSummary with native view
    if (this.$route.params.fromExtensions === true || this.$route.params.viewMode === 'native' || this.$route.query.viewMode === 'native') {
      if (this.isCurrentNetworkNativeFeatureSelected) {
        console.log('[StepConnectPages] Setting viewMode to native from route')
        this.viewMode = 'native'
      } else {
        console.log('[StepConnectPages] Native feature not selected for current network, remaining in API view')
        this.viewMode = 'api'
      }
    }
    
    // Restore state after OAuth redirect
    this.restoreOnboardingState()
    
    // Load existing pages from Dashboard for selected networks
    if (localStorage.getItem('onboarding_mode') !== 'true') {
      await this.loadExistingPages()
    }
    
    // Load selected pages for current network (only if not in OAuth return mode)
    if (localStorage.getItem('onboarding_mode') !== 'true') {
      this.loadSelectedPages()
      
      // OAuth redirects are handled in restoreOnboardingState() after state restoration
      // No need to call handleOAuthRedirects() here to avoid duplicate processing
      console.log('[StepConnectPages] Not in OAuth return mode - state loaded')
    }
  },

  async beforeRouteLeave(to, from, next) {
    try {
      if (this.isSavingNetwork) {
        while (this.isSavingNetwork) {
          await new Promise(resolve => setTimeout(resolve, 100))
        }
      } else if (this.currentNetwork) {
        await this.syncCurrentNetworkToBackend()
      }
    } catch (err) {
      console.error('[StepConnectPages] Error in beforeRouteLeave sync:', err)
    }
    next()
  },

  beforeDestroy() {
    window.removeEventListener('pageshow', this.handlePageShow)
    // Clean up any OAuth parameters
    const params = new URLSearchParams(location.search)
    if (params.get('code') || params.get('oauth_token') || params.get('state')) {
      history.replaceState({}, document.title, location.pathname + location.hash)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../variables.scss';

.onboarding-connect-pages {
  display: flex;
  min-height: 100vh;
}

.onboarding-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 64px;
  overflow-y: auto;
  overflow-x: hidden;

  &::after {
    content: '';
    display: block;
    height: 100px;
    flex-shrink: 0;
  }
}

/* Header section */
.onboarding-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 20px;
  margin-bottom: 24px;

  .onboarding-network-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: $bg-ui-selected;
    padding: 12px;
  }

  .onboarding-header-content {
    flex: 1;
  }

  .onboarding-header-title {
    font-size: 28px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 8px 0;

    .native-icon {
      display: inline-block;
      vertical-align: -4px;
      margin-left: -6px;
    }
  }

  .onboarding-header-subtitle {
    font-size: 16px;
    color: #6b7280;
    margin: 0;
    line-height: 1.6;

      strong {
        color: $primary-color;
        font-weight: 600;
      }
    }
}

.onboarding-features-badges {
  display: flex;
  flex-wrap: wrap;
  overflow: visible;
  justify-content: center;
  gap: 8px;
  margin-bottom: 32px;

  .onboarding-feature-badge {
    position: relative;
    padding: 6px 12px;
    background: $bg-ui-selected;
    color: $primary-color;
    font-size: 13px;
    font-weight: 500;
    border-radius: 16px;
    text-align: center;
    white-space: nowrap;
    transition: all 0.1s ease;

    &:hover {
      transform: translateY(-1px);
    }

    &[data-tooltip]:not([data-tooltip=""]) {
      cursor: default;

      &::before {
        content: attr(data-tooltip);
        position: absolute;
        bottom: calc(100% + 8px);
        left: 50%;
        transform: translateX(-50%) translateY(-4px);
        padding: 6px 10px;
        background: #424242;
        color: #fff;
        font-size: 12px;
        font-weight: 500;
        border-radius: 6px;
        white-space: normal;
        max-width: 300px;
        min-width: 120px;
        text-align: center;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.1s ease, transform 0.1s ease;
        z-index: 1000;
      }

      &::after {
        content: '';
        position: absolute;
        bottom: calc(100% - 2px);
        left: 50%;
        transform: translateX(-50%) translateY(-4px);
        border: 6px solid transparent;
        border-top-color: #424242;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.1s ease, transform 0.1s ease;
        z-index: 1000;
      }

      &:hover::before,
      &:hover::after {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
      }
    }
  }
}

.onboarding-quota-warning {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  margin-bottom: 24px;
  max-width: 600px;
  color: #92400e;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }

  strong {
    display: block;
    margin-bottom: 4px;
    font-weight: 600;
  }

  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
  }

  a {
    color: #b45309;
    text-decoration: underline;
    font-weight: 500;

    &:hover {
      color: #92400e;
    }
  }
}

.onboarding-pages-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(280px, 1fr));
  gap: 16px;
  min-height: 80px;
  max-height: 440px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 50px 12px 16px 4px;
  margin-bottom: 24px;
  max-width: 600px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;

    &:hover {
      background: #94a3b8;
    }
  }
}

.onboarding-page-card {
  position: relative;
  background: #ffffff;
  border: 1px solid $border-ui;
  border-radius: 12px;
  padding: 16px;

  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 1;

  &:hover,
  &.has-active-tooltip {
    border-color: $primary-color;
    z-index: 999;
  }

  &.selected {
    border-color: $primary-color;
    background: $bg-ui-selected;

    .avatar-placeholder {
      background-color: $primary-color;
      color: $bg-ui-selected;
    }
  }

  &.pre-existing {
    border-color: #d1d5db;
    background: #fafafa;
    padding-top: 28px;

    .pre-existing-badge {
      top: 8px;
      left: 12px;
    }

    &:hover {
      border-color: #d1d5db;
      cursor: default;
    }

    &.selected {
      border-color: $primary-color;
      background: $bg-ui-selected;
    }
  }

  &.non-deselectable {
    cursor: not-allowed;
    
    &:hover {
      border-color: $primary-color;
    }
  }

  &.native-view-card {
    cursor: default;

    &:hover {
      border-color: $primary-color;
    }
  }

  &.onboarding-add-page-card {
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    padding: 20px;
    border: 1px dashed #d1d5db;
    background: #f9fafb;
    gap: 16px;

    &:hover:not(.disabled) {
      border-color: $primary-color;
      background: #ffffff;
    }

    &.disabled {
      opacity: 0.5;
      cursor: not-allowed;
      
      .onboarding-add-icon {
        background: #e5e7eb;
        color: #9ca3af;
      }

      .onboarding-add-text {
        color: #9ca3af;
      }
    }

    .onboarding-add-icon {
      width: 32px;
      height: 32px;
      flex-shrink: 0;
      border-radius: 50%;
      background: $bg-ui-selected;
      color: $primary-color;
      font-size: 20px;
      font-weight: 600;
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 1;

      svg.native-icon {
        width: 16px;
        height: 16px;
        fill: $primary-color;
      }
    }

    .onboarding-add-text {
      font-size: 15px;
      color: #111827;
      display: flex;
      flex-direction: column;
      gap: 3px;
      text-align: left;
      line-height: 1.2;

      span {
        line-height: 1.2;
      }

      .card-title {
        color: #111827;
        line-height: 1.1;
        letter-spacing: 0;
        text-align: left;
      }

      .card-subtitle {
        font-size: 12px;
        color: #6b7280;
        line-height: 1.3;
      }

      .card-badges {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        margin-top: 4px;

        .badge-mini {
          font-size: 12px;
          padding: 2px 7px;
          border-radius: 12px;
          background: $bg-ui-selected;
          color: $primary-color;
          font-weight: 500;
        }
      }
    }
  }
}

.onboarding-page-card-main {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
}

.onboarding-page-native-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding-top: 2px;
}

.pre-existing-badge {
  position: absolute;
  top: 6px;
  left: 6px;
  padding: 4px 8px;
  background: #e5d8e093;
  color: $primary-color;
  font-size: 10px;
  font-weight: 600;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  z-index: 1;
}

.onboarding-page-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
}

.onboarding-page-info {
  flex: 1;
  min-width: 0; // Force flex item to respect text-overflow
  overflow: hidden; // Prevent content from overflowing

  .onboarding-page-name {
    font-size: 15px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 4px;
    overflow: hidden; // Ensure text does not overflow
    text-overflow: ellipsis; // Add ellipsis for overflowed text
    white-space: nowrap; // Prevent text from wrapping
  }

  .onboarding-page-username {
    font-size: 13px;
    color: #6b7280;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    // max-width: 180px;
  }
}

.onboarding-selection-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px; // Fixed width to prevent overflow
  height: 24px; // Fixed height to prevent overflow
}

.onboarding-selection-check {
  width: 24px;
  height: 24px;
  border: 1px solid $border-ui;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  position: relative;

  &::before {
    content: '✓';
    display: none;
    color: white;
    font: bold 14px/1 sans-serif;
    font-weight: 500;
  }

  &.checked {
    background-color: $primary-color;
    border: none;
    border-color: $primary-color;

    &::before {
      display: block;
    }
  }
}

/* Footer section */
.onboarding-footer-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-left: 400px;

  &.is-native-view {
    margin-top: 50px;
  }
}

.onboarding-pages-counter {
  display: flex;
  flex-direction: column;
  margin-top: 10px;
  margin-bottom: 60px;
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;

  & :nth-last-child(2) {
    margin-top: 8px;
  }
}

.onboarding-footer {
  display: flex;
  align-items: center;
  gap: 12px;

  .onboarding-btn-secondary,
  .onboarding-btn-primary {
    padding: 12px 24px;
    font-size: 15px;
    font-weight: 600;
    border-radius: 20px;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
  }

  .onboarding-btn-secondary {
    background: transparent;
    color: #6b7280;

    &:hover {
      color: #111827;
    }
  }

  .onboarding-btn-primary {
    background: $bg-ui-selected;
    color: $primary-color;
    display: flex;
    align-items: center;
    gap: 12px;

    &:hover:not(:disabled) {
      background: $primary-color;
      color: #ffffff;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .onboarding-step-indicator {
      font-size: 13px;
      opacity: 0.8;
    }
  }
}

.avatar-placeholder {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: $bg-ui-selected;
  color: $primary-color;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 18px;
  flex-shrink: 0;
}

.onboarding-page-native-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-top: 4px;
}

.onboarding-native-badge-active {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  background-color: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  color: #047857;

  .native-icon-active {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
}

.onboarding-native-btn-wrapper {
  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
}

.onboarding-native-action-btn-container {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.onboarding-native-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 16px;
  background-color: #fdf2f8;
  border: 1px solid #6a1f4c;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  color: #6a1f4c;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;

  .native-icon-btn {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    fill: currentColor;
  }

  &:hover {
    background-color: #6a1f4c;
    color: #fff;
    box-shadow: 0 2px 6px rgba(106, 31, 76, 0.15);
  }
}

.onboarding-native-info-icon {
  position: absolute;
  top: -4px;
  right: -5px;
  width: 18px;
  height: 18px;
  // background-color: #fff;
  cursor: pointer;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.12));
  transition: transform 0.2s ease;
  z-index: 10;


  &:hover {
    transform: scale(1.2);
    z-index: 100;
  }
}

.onboarding-native-hover-tooltip {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 99999;
  width: 220px;
  padding: 8px 10px;
  background-color: #ffffff;
  color: #6a1f4c;
  border-radius: 8px;
  box-shadow: 0 6px 16px rgba(106, 31, 76, 0.18);
  pointer-events: none;
  text-align: center;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-width: 5px;
    border-style: solid;
    border-color: #ffffff transparent transparent transparent;
  }

  .onboarding-tooltip-title {
    font-weight: 600;
    margin-bottom: 8px;
    color: #6a1f4c;
    font-size: 11px;
    line-height: 1.2;
  }

  .onboarding-tooltip-badges {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
  }

  .onboarding-mini-badge {
    background-color: #f1edf5;
    padding: 3px 6px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
    color: #6a1f4c;
    white-space: nowrap;
  }
}

@media (max-width: 1150px) {
  .onboarding-pages-list {
    grid-template-columns: repeat(2, minmax(200px, 1fr));
  }

  .onboarding-page-card {
    // margin-right: 0;
    margin: 0;
    &.onboarding-add-page-card {
      justify-content: center !important;
      align-items: center !important;
      .onboarding-add-text{
        display: none;
      }
    }

    .onboarding-selection-indicator {
      display: none;
    }

    .pre-existing-badge {
      display: none;
      // TODO : add corresponding Icon
    }  
  }

  .onboarding-footer-container {
    transform: translate(130px);
    margin-left: 0;
    // flex-wrap: nowrap;
  }
}

@media (max-width: 800px) {

  .onboarding-features-badges
    .onboarding-feature-badge {
        padding: 6px 5px;
    }

  .onboarding-pages-list {
    grid-template-columns: repeat(1, minmax(200px, 1fr));
  }

  .onboarding-page-card {
    // margin-right: 0;
    margin: 0;
    &.onboarding-add-page-card {
      justify-content: center !important;
      align-items: center !important;
      .onboarding-add-text{
        display: none;
      }
    }

    .onboarding-selection-indicator {
      display: none;
    }
  }
  
  .onboarding-pages-counter {
    transform: translate(0px);
  }

  .onboarding-footer-container {
    transform: translate(0px);
    
    .onboarding-step-indicator {
      display: none;
    }
  }

}

.btn-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-right: 8px;
  vertical-align: middle;
}

.modal-popup {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(18, 24, 38, 0.45);
  backdrop-filter: blur(2px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10001;

  .confirmation-popup {
    max-width: 440px;
    width: 90%;
    padding: 32px 28px;
    border-radius: 16px;
    background-color: #ffffff;
    text-align: center;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);

    .modal-warning-icon {
      width: 56px;
      height: 56px;
      background: #fffbeb;
      border: 1px solid #fef3c7;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 16px;
      color: #d97706;
    }

    .modal-title {
      font-size: 18px;
      font-weight: 700;
      color: #111827;
      margin-bottom: 10px;
    }

    .modal-text {
      font-size: 14px;
      color: #4b5563;
      line-height: 1.5;
      margin-bottom: 24px;
    }

    .modal-buttons {
      display: flex;
      gap: 12px;
      justify-content: center;

      .button {
        flex: 1;
        height: 44px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
      }

      .btn-outline-primary {
        border: 1px solid #d1d5db;
        color: #374151;
        background: #ffffff;

        &:hover {
          background: #f3f4f6;
        }
      }

      .btn-primary {
        background: $primary-color;
        color: #ffffff;
        border: none;

        &:hover {
          opacity: 0.9;
        }
      }
    }
  }
}
</style>
